import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatUtcToTz } from '../../utils/timezone';
import { Page, Layout, Card, BlockStack, Text, Button, Badge } from '@shopify/polaris';
import { AdobeCommerceCredentialsModal } from '../modals/credentials/AdobeCommerceCredentialsModal';
import NetsuiteM2MModal from '../modals/credentials/NetsuiteM2MModal';
import { ShopifyCredentialsModal } from '../modals/credentials/ShopifyCredentialsModal';
import { SageCredentialsModal } from '../modals/credentials/SageCredentialsModal';
import { BearerTokenCredentialsModal } from '../modals/credentials/BearerTokenCredentialsModal';
import { WayfairCredentialsModal } from '../modals/credentials/WayfairCredentialsModal';
import { WayfairAgentCredentialsModal } from '../modals/credentials/WayfairAgentCredentialsModal';
import { QuickBooksCredentialsModal } from '../modals/credentials/QuickBooksCredentialsModal';
import ConfigurationModal from '../modals/configuration/ConfigurationModal';
// Replaced legacy products modals with generic ConfigurationModal instances
// Removed ProductsPriceLevelModal in favor of generic ConfigurationModal
// Removed Orders-specific legacy modals in favor of generic ConfigurationModal
import { OrdersSettingsModalWrapper } from '../modals/orders/OrdersSettingsModalWrapper';
import { NetSuiteCashSalesSettingsModal } from '../modals/orders/NetSuiteCashSalesSettingsModal';
import { ProductSettingsModal } from '../modals/products/ProductSettingsModal';
import { PayoutSettingsModal } from '../modals/payouts/PayoutSettingsModal';
import { HelpSection } from '../common/HelpSection';
import { configApi } from '../../services/api/configApi';
import { resolveReturnsConfigKey } from '../../utils/resolveReturnsConfigKey';
import './ConfigurationTab.css';

export function ConfigurationTab() {
  const { currentWorkspace, isTenantContextReady } = useWorkspace();
  const { refresh } = useAuth();
  const keepAlive = useCallback(async () => {
    try { await refresh(); } catch (_) {}
  }, [refresh]);
  const [serviceGates, setServiceGates] = useState({
    products: false,
    inventory: false,
    prices: false,
    orders: false,
    qbo_customer_deposits: false,
    qbo_shipment_invoices: false,
    qbo_refunds: false,
    qbo_fee_journals: false,
    qbo_vendor_bills: false,
    order_updates: false,
    customers: false,
    images: false,
    fulfillment: false,
    fulfillments: false,
    fulfilled_orders: false,
    returns: false,
    returnsKey: null,
    refunds: false,
    cash_sales: false,
    cash_refunds: false,
    exchanges: false,
    payouts: false,
    payout_settlements: false
  });
  const [gatesReady, setGatesReady] = useState(false);
  const tz = currentWorkspace?.config?.timezone || 'America/New_York';
  const [adobeModalOpen, setAdobeModalOpen] = useState(false);
  const [netsuiteM2MOpen, setNetsuiteM2MOpen] = useState(false);
  const [netsuiteM2MConfig, setNetsuiteM2MConfig] = useState(null);
  const [shopifyModalOpen, setShopifyModalOpen] = useState(false);
  const [sageModalOpen, setSageModalOpen] = useState(false);
  const [kdsModalOpen, setKdsModalOpen] = useState(false);
  const [wayfairModalOpen, setWayfairModalOpen] = useState(false);
  const [wayfairAgentModalOpen, setWayfairAgentModalOpen] = useState(false);
  const [quickBooksModalOpen, setQuickBooksModalOpen] = useState(false);
  const [shopifyCredentials, setShopifyCredentials] = useState(null);
  const [sageCredentials, setSageCredentials] = useState(null);
  const [kdsCredentials, setKdsCredentials] = useState(null);
  const [wayfairCredentials, setWayfairCredentials] = useState(null);
  const [wayfairAgentCredentials, setWayfairAgentCredentials] = useState(null);
  const [quickBooksCredentials, setQuickBooksCredentials] = useState(null);
  const [customersModalOpen, setCustomersModalOpen] = useState(false);

  const [itemsConfigModalOpen, setItemsConfigModalOpen] = useState(false);
  const [productSettingsModalOpen, setProductSettingsModalOpen] = useState(false);
  const [inventoryConfigModalOpen, setInventoryConfigModalOpen] = useState(false);
  const [priceLevelsConfigModalOpen, setPriceLevelsConfigModalOpen] = useState(false);
  // Removed orders general modal in favor of generic orders ConfigurationModal
  const [ordersConfigModalOpen, setOrdersConfigModalOpen] = useState(false);
  const [qboCustomerDepositsModalOpen, setQboCustomerDepositsModalOpen] = useState(false);
  const [qboShipmentInvoicesModalOpen, setQboShipmentInvoicesModalOpen] = useState(false);
  const [qboRefundsModalOpen, setQboRefundsModalOpen] = useState(false);
  const [qboFeeJournalsModalOpen, setQboFeeJournalsModalOpen] = useState(false);
  const [qboVendorBillsModalOpen, setQboVendorBillsModalOpen] = useState(false);
  const [orderUpdatesModalOpen, setOrderUpdatesModalOpen] = useState(false);
  const [ordersSettingsModalOpen, setOrdersSettingsModalOpen] = useState(false);
  const [ordersSettingsState, setOrdersSettingsState] = useState({ order: {} });
  const [imagesModalOpen, setImagesModalOpen] = useState(false); // deprecated; images disabled
  const [imagesConfig, setImagesConfig] = useState(null);
  const [fulfillmentModalOpen, setFulfillmentModalOpen] = useState(false);
  const [fulfillmentModalKey, setFulfillmentModalKey] = useState('fulfillments');
  const [returnsModalOpen, setReturnsModalOpen] = useState(false);
  const [refundsModalOpen, setRefundsModalOpen] = useState(false);
  const [cashSalesModalOpen, setCashSalesModalOpen] = useState(false);
  const [cashSalesSettingsModalOpen, setCashSalesSettingsModalOpen] = useState(false);
  const [cashSalesSettingsState, setCashSalesSettingsState] = useState({ order: {} });
  const [cashRefundsModalOpen, setCashRefundsModalOpen] = useState(false);
  const [exchangesModalOpen, setExchangesModalOpen] = useState(false);
  const [payoutsModalOpen, setPayoutsModalOpen] = useState(false);
  const [payoutSettingsModalOpen, setPayoutSettingsModalOpen] = useState(false);
  const [payoutSettingsState, setPayoutSettingsState] = useState({ payout: {} });
  const [payoutSettlementsModalOpen, setPayoutSettlementsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [adobeCredentials, setAdobeCredentials] = useState(null);

  // Configuration status tracking - all start as incomplete
  const [configStatus, setConfigStatus] = useState({
    netsuite: {
      hasCredentials: false,
      lastUpdated: null,
      status: 'incomplete' // 'incomplete', 'complete', 'error'
    },
    adobe: {
      hasCredentials: false,
      lastUpdated: null,
      status: 'incomplete'
    },
    shopify: {
      hasCredentials: false,
      lastUpdated: null,
      status: 'incomplete'
    },
    sage: {
      hasCredentials: false,
      lastUpdated: null,
      status: 'incomplete'
    },
    kds: {
      hasCredentials: false,
      lastUpdated: null,
      status: 'incomplete'
    },
    wayfair: {
      hasCredentials: false,
      lastUpdated: null,
      status: 'incomplete'
    },
    wayfairagent: {
      hasCredentials: false,
      lastUpdated: null,
      status: 'incomplete'
    },
    quickbooks: {
      hasCredentials: false,
      lastUpdated: null,
      status: 'incomplete'
    },
    customers: {
      hasConfig: false,
      lastUpdated: null,
      status: 'incomplete',
      formId: null
    },
    products: {
      hasFieldMap: false,
      hasMatrixItemsFieldMap: false,
      hasInventory: false,
      hasPriceLevels: false,
      lastUpdated: null,
      status: 'incomplete'
    },
    orders: {
      hasConfig: false,
      dto: null,
      sourceSample: null,
      hasFieldMap: false,
      hasGeneral: false,
      hasTax: false,
      hasDiscount: false,
      hasPayment: false,
      hasShipping: false,
      lastUpdated: null,
      status: 'incomplete'
    },
    qbo_customer_deposits: {
      hasConfig: false,
      dto: null,
      sourceSample: null,
      lastUpdated: null,
      status: 'incomplete'
    },
    qbo_shipment_invoices: {
      hasConfig: false,
      dto: null,
      sourceSample: null,
      lastUpdated: null,
      status: 'incomplete'
    },
    qbo_refunds: {
      hasConfig: false,
      dto: null,
      sourceSample: null,
      lastUpdated: null,
      status: 'incomplete'
    },
    qbo_fee_journals: {
      hasConfig: false,
      dto: null,
      sourceSample: null,
      lastUpdated: null,
      status: 'incomplete'
    },
    qbo_vendor_bills: {
      hasConfig: false,
      dto: null,
      sourceSample: null,
      lastUpdated: null,
      status: 'incomplete'
    },
    order_updates: {
      hasConfig: false,
      dto: null,
      sourceSample: null,
      lastUpdated: null,
      status: 'incomplete'
    },
    images: {
      hasSavedSearch: false,
      lastUpdated: null,
      status: 'incomplete'
    },
    fulfillment: {
      hasSavedSearch: false,
      lastUpdated: null,
      status: 'incomplete'
    },
    fulfillments: {
      hasSavedSearch: false,
      dto: null,
      sourceSample: null,
      lastUpdated: null,
      status: 'incomplete'
    },
    fulfilled_orders: {
      hasSavedSearch: false,
      dto: null,
      sourceSample: null,
      lastUpdated: null,
      status: 'incomplete'
    },
    returns: {
      hasSavedSearch: false,
      lastUpdated: null,
      status: 'incomplete'
    },
    wayfair_returns: {
      hasSavedSearch: false,
      lastUpdated: null,
      status: 'incomplete'
    },
    refunds: {
      hasSavedSearch: false,
      lastUpdated: null,
      status: 'incomplete'
    },
    cash_sales: {
      hasSavedSearch: false,
      lastUpdated: null,
      status: 'incomplete'
    },
    cash_refunds: {
      hasSavedSearch: false,
      lastUpdated: null,
      status: 'incomplete'
    },
    exchanges: {
      hasSavedSearch: false,
      lastUpdated: null,
      status: 'incomplete'
    },
    payouts: {
      hasSavedSearch: false,
      lastUpdated: null,
      status: 'incomplete'
    },
    payout_settlements: {
      hasSavedSearch: false,
      lastUpdated: null,
      status: 'incomplete'
    }
  });

  const returnsConfigKey = serviceGates.returnsKey || 'returns';
  const activeReturnsStatus = configStatus[returnsConfigKey] || configStatus.returns;

  // Calculate overall configuration status
  const getOverallCounts = () => {
    const checks = [];
    const requiredCredentialProviders = [
      currentWorkspace?.integrations?.frontend?.provider,
      currentWorkspace?.integrations?.backend?.provider
    ].filter(Boolean);
    requiredCredentialProviders.forEach((provider) => {
      checks.push(!!configStatus[provider]?.hasCredentials);
    });
    if (serviceGates.products) {
      checks.push(!!configStatus.products?.hasFieldMap);
      checks.push(!!configStatus.products?.hasMatrixItemsFieldMap);
    }
    if (serviceGates.inventory) {
      checks.push(!!configStatus.products?.hasInventory);
    }
    if (serviceGates.prices) {
      checks.push(!!configStatus.products?.hasPriceLevels);
    }
    if (serviceGates.orders) {
      checks.push(!!configStatus.orders?.hasConfig);
    }
    if (serviceGates.qbo_customer_deposits) {
      checks.push(!!configStatus.qbo_customer_deposits?.hasConfig);
    }
    if (serviceGates.qbo_shipment_invoices) {
      checks.push(!!configStatus.qbo_shipment_invoices?.hasConfig);
    }
    if (serviceGates.qbo_refunds) {
      checks.push(!!configStatus.qbo_refunds?.hasConfig);
    }
    if (serviceGates.qbo_fee_journals) {
      checks.push(!!configStatus.qbo_fee_journals?.hasConfig);
    }
    if (serviceGates.qbo_vendor_bills) {
      checks.push(!!configStatus.qbo_vendor_bills?.hasConfig);
    }
    if (serviceGates.order_updates) {
      checks.push(!!configStatus.order_updates?.hasConfig);
    }
    if (serviceGates.customers) {
      checks.push(!!configStatus.customers?.hasConfig);
    }
    if (serviceGates.images) {
      checks.push(!!configStatus.images?.hasSavedSearch);
    }
    if (serviceGates.fulfillment) {
      checks.push(!!configStatus.fulfillment?.hasSavedSearch);
    }
    if (serviceGates.returns) {
      checks.push(!!activeReturnsStatus?.hasSavedSearch);
    }
    if (serviceGates.refunds) {
      checks.push(!!configStatus.refunds?.hasSavedSearch);
    }
    if (serviceGates.cash_sales) {
      checks.push(!!configStatus.cash_sales?.hasSavedSearch);
    }
    if (serviceGates.cash_refunds) {
      checks.push(!!configStatus.cash_refunds?.hasSavedSearch);
    }
    if (serviceGates.exchanges) {
      checks.push(!!configStatus.exchanges?.hasSavedSearch);
    }
    if (serviceGates.payouts) {
      checks.push(!!configStatus.payouts?.hasSavedSearch);
    }
    if (serviceGates.payout_settlements) {
      checks.push(!!configStatus.payout_settlements?.hasSavedSearch);
    }
    const total = checks.length;
    const completed = checks.filter(Boolean).length;
    let status = 'incomplete';
    if (completed === total && total > 0) status = 'complete';
    else if (completed > 0) status = 'partial';
    return { completed, total, status };
  };

  // Calculate products configuration status based on all product configs
  const getProductsStatus = (productsState = configStatus.products) => {
    // Safety check to prevent errors when state is undefined
    if (!productsState) {
      return 'incomplete';
    }

    const { hasFieldMap, hasMatrixItemsFieldMap, hasInventory, hasPriceLevels } = productsState;

    if (hasFieldMap && hasMatrixItemsFieldMap && hasInventory && hasPriceLevels) {
      return 'complete';
    } else if (hasFieldMap || hasMatrixItemsFieldMap || hasInventory || hasPriceLevels) {
      return 'partial';
    } else {
      return 'incomplete';
    }
  };

  // Calculate orders configuration status based on all order configs
  const getOrdersStatus = (ordersState = configStatus.orders) => {
    // Safety check to prevent errors when state is undefined
    if (!ordersState) {
      return 'incomplete';
    }

    const { hasFieldMap, hasGeneral, hasTax, hasDiscount, hasPayment, hasShipping } = ordersState;

    if (hasFieldMap && hasGeneral && hasTax && hasDiscount && hasPayment && hasShipping) {
      return 'complete';
    } else if (hasFieldMap || hasGeneral || hasTax || hasDiscount || hasPayment || hasShipping) {
      return 'partial';
    } else {
      return 'incomplete';
    }
  };

  // Calculate images configuration status
  const getImagesStatus = (imagesState = configStatus.images) => {
    // Safety check to prevent errors when state is undefined
    if (!imagesState) {
      return 'incomplete';
    }

    const { hasSavedSearch } = imagesState;

    if (hasSavedSearch) {
      return 'complete';
    } else {
      return 'incomplete';
    }
  };

  // Calculate fulfillment configuration status
  const getFulfillmentStatus = (fulfillmentState = configStatus.fulfillment) => {
    // Safety check to prevent errors when state is undefined
    if (!fulfillmentState) {
      return 'incomplete';
    }

    const { hasSavedSearch } = fulfillmentState;

    if (hasSavedSearch) {
      return 'complete';
    } else {
      return 'incomplete';
    }
  };

  const getOverallStatusText = () => {
    const { status } = getOverallCounts();
    switch (status) {
      case 'complete':
        return 'All configurations are complete';
      case 'partial':
        return 'Some configurations are incomplete';
      case 'incomplete':
        return 'No configurations are complete';
      default:
        return 'Configuration status unknown';
    }
  };

  // applyStatus must be defined BEFORE the useEffect that uses it (line 302)
  const applyStatus = useCallback((key, configured, updatedAt) => {
    switch ((key || '').toLowerCase()) {
      case 'netsuite':
        setConfigStatus(prev => ({
          ...prev,
          netsuite: { hasCredentials: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'adobe':
        setConfigStatus(prev => ({
          ...prev,
          adobe: { hasCredentials: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'customers':
        setConfigStatus(prev => ({
          ...prev,
          customers: { ...prev.customers, hasConfig: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'orders':
        setConfigStatus(prev => ({
          ...prev,
          orders: { ...prev.orders, hasConfig: configured, lastUpdated: updatedAt }
        }));
        break;
      case 'qbo_customer_deposits':
        setConfigStatus(prev => ({
          ...prev,
          qbo_customer_deposits: { ...prev.qbo_customer_deposits, hasConfig: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'qbo_shipment_invoices':
        setConfigStatus(prev => ({
          ...prev,
          qbo_shipment_invoices: { ...prev.qbo_shipment_invoices, hasConfig: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'qbo_refunds':
        setConfigStatus(prev => ({
          ...prev,
          qbo_refunds: { ...prev.qbo_refunds, hasConfig: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'qbo_fee_journals':
        setConfigStatus(prev => ({
          ...prev,
          qbo_fee_journals: { ...prev.qbo_fee_journals, hasConfig: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'qbo_vendor_bills':
        setConfigStatus(prev => ({
          ...prev,
          qbo_vendor_bills: { ...prev.qbo_vendor_bills, hasConfig: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'order_updates':
        setConfigStatus(prev => ({
          ...prev,
          order_updates: { ...prev.order_updates, hasConfig: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'products':
        setConfigStatus(prev => ({
          ...prev,
          products: { ...prev.products, hasFieldMap: configured, hasProductSettings: configured, lastUpdated: updatedAt, status: getProductsStatus({ ...prev.products, hasFieldMap: configured }) }
        }));
        break;
      case 'matrixitems':
        setConfigStatus(prev => ({
          ...prev,
          products: { ...prev.products, hasMatrixItemsFieldMap: configured, lastUpdated: updatedAt, status: getProductsStatus({ ...prev.products, hasMatrixItemsFieldMap: configured }) }
        }));
        break;
      case 'inventory':
        setConfigStatus(prev => ({
          ...prev,
          products: { ...prev.products, hasInventory: configured, lastUpdated: updatedAt, status: getProductsStatus({ ...prev.products, hasInventory: configured }) }
        }));
        break;
      case 'pricelevels':
        setConfigStatus(prev => ({
          ...prev,
          products: { ...prev.products, hasPriceLevels: configured, lastUpdated: updatedAt, status: getProductsStatus({ ...prev.products, hasPriceLevels: configured }) }
        }));
        break;
      case 'images':
        setConfigStatus(prev => ({
          ...prev,
          images: { ...prev.images, hasSavedSearch: configured, lastUpdated: updatedAt, status: getImagesStatus({ ...prev.images, hasSavedSearch: configured }) }
        }));
        break;
      case 'fulfillment':
      case 'fulfillments':
      case 'fulfilled_orders':
        setConfigStatus(prev => ({
          ...prev,
          [(key || '').toLowerCase()]: {
            ...(prev[(key || '').toLowerCase()] || prev.fulfillment),
            hasSavedSearch: configured,
            lastUpdated: updatedAt,
            status: getFulfillmentStatus({ ...(prev[(key || '').toLowerCase()] || prev.fulfillment), hasSavedSearch: configured })
          },
          fulfillment: {
            ...prev.fulfillment,
            hasSavedSearch: configured || prev.fulfillments?.hasSavedSearch || prev.fulfilled_orders?.hasSavedSearch,
            lastUpdated: updatedAt || prev.fulfillment?.lastUpdated,
            status: getFulfillmentStatus({
              ...prev.fulfillment,
              hasSavedSearch: configured || prev.fulfillments?.hasSavedSearch || prev.fulfilled_orders?.hasSavedSearch
            })
          }
        }));
        break;
      case 'returns':
        setConfigStatus(prev => ({
          ...prev,
          returns: { ...prev.returns, hasSavedSearch: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'wayfair_returns':
        setConfigStatus(prev => ({
          ...prev,
          wayfair_returns: { ...prev.wayfair_returns, hasSavedSearch: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'refunds':
        setConfigStatus(prev => ({
          ...prev,
          refunds: { ...prev.refunds, hasSavedSearch: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'cash_sales':
        setConfigStatus(prev => ({
          ...prev,
          cash_sales: { ...prev.cash_sales, hasSavedSearch: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'cash_refunds':
        setConfigStatus(prev => ({
          ...prev,
          cash_refunds: { ...prev.cash_refunds, hasSavedSearch: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'exchanges':
        setConfigStatus(prev => ({
          ...prev,
          exchanges: { ...prev.exchanges, hasSavedSearch: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'payouts':
        setConfigStatus(prev => ({
          ...prev,
          payouts: { ...prev.payouts, hasSavedSearch: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'payout_settlements':
        setConfigStatus(prev => ({
          ...prev,
          payout_settlements: { ...prev.payout_settlements, hasSavedSearch: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'shopify':
        setConfigStatus(prev => ({
          ...prev,
          shopify: { hasCredentials: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'sage':
        setConfigStatus(prev => ({
          ...prev,
          sage: { hasCredentials: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'kds':
        setConfigStatus(prev => ({
          ...prev,
          kds: { hasCredentials: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'wayfair':
        setConfigStatus(prev => ({
          ...prev,
          wayfair: { hasCredentials: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'wayfairagent':
        setConfigStatus(prev => ({
          ...prev,
          wayfairagent: { hasCredentials: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      case 'quickbooks':
        setConfigStatus(prev => ({
          ...prev,
          quickbooks: { hasCredentials: configured, lastUpdated: updatedAt, status: configured ? 'complete' : 'incomplete' }
        }));
        break;
      default:
        break;
    }
  }, [getFulfillmentStatus, getImagesStatus, getProductsStatus]);

  // Consolidated initial load: services + statuses fetched once per workspace
  const initializedWorkspaceIdRef = useRef(null);
  useEffect(() => {
    if (!isTenantContextReady || !currentWorkspace?.id) return;
    if (initializedWorkspaceIdRef.current === currentWorkspace.id) return;
    initializedWorkspaceIdRef.current = currentWorkspace.id;
    (async () => {
      try {
        await keepAlive();
        localStorage.removeItem('shopifyAdminConfigs');
        const [svcResp, statusResp] = await Promise.all([
          configApi.getServices(),
          configApi.getAllStatuses()
        ]);
        if (svcResp?.success) {
          const list = svcResp.data || [];
          const statusKeys = new Set(
            Object.entries(statusResp?.statuses || {})
              .filter(([, status]) => status?.configured)
              .map(([key]) => String(key || '').toLowerCase())
          );
          const keys = new Set(
            list
              .filter(s => s.allocated !== false && s?.ui?.showTab !== false)
              .map(s => (s.key || '').toLowerCase())
          );
          const listedKeys = new Set(list.map(s => (s.key || '').toLowerCase()));
          statusKeys.forEach((key) => {
            if (!listedKeys.has(key)) keys.add(key);
          });
          const returnsKey = resolveReturnsConfigKey(keys);
          const gates = {
            products: keys.has('products'),
            inventory: keys.has('inventory'),
            prices: keys.has('pricelevels'),
            orders: keys.has('orders'),
            qbo_customer_deposits: keys.has('qbo_customer_deposits'),
            qbo_shipment_invoices: keys.has('qbo_shipment_invoices'),
            qbo_refunds: keys.has('qbo_refunds'),
            qbo_fee_journals: keys.has('qbo_fee_journals'),
            qbo_vendor_bills: keys.has('qbo_vendor_bills'),
            order_updates: keys.has('order_updates'),
            customers: keys.has('customers'),
            images: keys.has('images'),
            fulfillment: keys.has('fulfillment') || keys.has('fulfillments') || keys.has('fulfilled_orders'),
            fulfillments: keys.has('fulfillments'),
            fulfilled_orders: keys.has('fulfilled_orders'),
            returns: !!returnsKey,
            returnsKey,
            refunds: keys.has('refunds'),
            cash_sales: keys.has('cash_sales'),
            cash_refunds: keys.has('cash_refunds'),
            exchanges: keys.has('exchanges'),
            payouts: keys.has('payouts'),
            payout_settlements: keys.has('payout_settlements')
          };
          setServiceGates(gates);
          setGatesReady(true);
        }
        if (statusResp?.success && statusResp.statuses) {
          const map = statusResp.statuses;
          Object.entries(map).forEach(([k, v]) => {
            if (v) applyStatus(k, !!v.configured, v.updatedAt || null);
          });
        }
      } catch (e) {
        // Initial configuration load failed
      }
    })();
  }, [isTenantContextReady, currentWorkspace?.id, keepAlive, applyStatus]);

  const prefetchStatuses = async (gates) => {
    try {
      const resp = await configApi.getAllStatuses();
      if (!resp?.success) return;
      const map = resp.statuses || {};
      const wantKeys = [];
      if (true) { wantKeys.push('netsuite', 'adobe'); }
      if (gates.orders) wantKeys.push('orders');
      if (gates.qbo_customer_deposits) wantKeys.push('qbo_customer_deposits');
      if (gates.qbo_shipment_invoices) wantKeys.push('qbo_shipment_invoices');
      if (gates.qbo_refunds) wantKeys.push('qbo_refunds');
      if (gates.qbo_fee_journals) wantKeys.push('qbo_fee_journals');
      if (gates.qbo_vendor_bills) wantKeys.push('qbo_vendor_bills');
      if (gates.order_updates) wantKeys.push('order_updates');
      if (gates.customers) wantKeys.push('customers');
      if (gates.products) wantKeys.push('products');
      if (gates.inventory) wantKeys.push('inventory');
      if (gates.prices) wantKeys.push('pricelevels');
      if (gates.images) wantKeys.push('images');
      if (gates.fulfillment) wantKeys.push('fulfilled_orders', 'fulfillments', 'fulfillment');
      if (gates.returnsKey) wantKeys.push(gates.returnsKey);
      if (gates.refunds) wantKeys.push('refunds');
      if (gates.cash_sales) wantKeys.push('cash_sales');
      if (gates.cash_refunds) wantKeys.push('cash_refunds');
      if (gates.exchanges) wantKeys.push('exchanges');
      if (gates.payouts) wantKeys.push('payouts');
      if (gates.payout_settlements) wantKeys.push('payout_settlements');
      wantKeys.forEach((k) => {
        const s = map[k];
        if (s) applyStatus(k, !!s.configured, s.updatedAt || null);
      });
    } catch (err) {
      // prefetchStatuses error
    }
  };

  // Removed duplicate - moved above

  const loadSavedConfigurations = async () => {
    try {
      setIsLoading(true);

      // Refresh credentials statuses
      try {
        const resp = await configApi.getAllStatuses();
        if (resp?.success && resp.statuses) {
          const statuses = resp.statuses;
          if (statuses.netsuite) {
            applyStatus('netsuite', !!statuses.netsuite.configured, statuses.netsuite.updatedAt || null);
          }
          if (statuses.adobe) {
            applyStatus('adobe', !!statuses.adobe.configured, statuses.adobe.updatedAt || null);
          }
          if (statuses.shopify) {
            applyStatus('shopify', !!statuses.shopify.configured, statuses.shopify.updatedAt || null);
          }
          if (statuses.sage) {
            applyStatus('sage', !!statuses.sage.configured, statuses.sage.updatedAt || null);
          }
          if (statuses.kds) {
            applyStatus('kds', !!statuses.kds.configured, statuses.kds.updatedAt || null);
          }
          if (statuses.wayfair) {
            applyStatus('wayfair', !!statuses.wayfair.configured, statuses.wayfair.updatedAt || null);
          }
          if (statuses.wayfairagent) {
            applyStatus('wayfairagent', !!statuses.wayfairagent.configured, statuses.wayfairagent.updatedAt || null);
          }
          if (statuses.quickbooks) {
            applyStatus('quickbooks', !!statuses.quickbooks.configured, statuses.quickbooks.updatedAt || null);
          }
        }
      } catch (e) {
        // Failed to refresh credential statuses
      }

      // Load orders DTO to get destination for modal routing
      try {
        const ordersResp = await configApi.getConfigByKey('orders');
        if (ordersResp?.success && ordersResp.data) {
          setConfigStatus(prev => ({
            ...prev,
            orders: {
              ...prev.orders,
              dto: ordersResp.data?.dto || null,
              sourceSample: ordersResp.data?.defaultSource || null
            }
          }));
        }
      } catch (e) {
        // Failed to load orders DTO
      }
    } catch (error) {
      // Error loading configuration status
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdobeSave = async (type, credentials) => {
    try {
      // Filter out empty or masked fields to keep existing values
      const filteredCredentials = {};
      Object.keys(credentials).forEach(key => {
        if (credentials[key] && credentials[key] !== '****' && credentials[key] !== '') {
          filteredCredentials[key] = credentials[key];
        }
      });

      // Ensure we have at least the required fields
      const minimalProvided = !!filteredCredentials.baseUrl || !!filteredCredentials.consumerKey || !!filteredCredentials.clientId || !!filteredCredentials.token;
      if (!minimalProvided) {
        alert('Please enter at least one credential field to update.');
        return;
      }

      if (type === 'adobe-paas') {
        // New PaaS format - save complete structure
        await configApi.saveAdobeCredentials(credentials); // Use original credentials, not filtered
      } else if (type === 'adobe-saas') {
        // New SaaS format - save complete structure
        await configApi.saveAdobeCredentials(credentials); // Use original credentials, not filtered
      } else if (type === 'adobe-token') {
        // Legacy token format
        await configApi.saveAdobeToken({ baseUrl: filteredCredentials.baseUrl, accessToken: filteredCredentials.accessToken });
      } else if (type === 'adobe-oauth2') {
        // Legacy OAuth2 format
        await configApi.saveAdobeOAuth2({ baseUrl: filteredCredentials.baseUrl, clientId: filteredCredentials.clientId, clientSecret: filteredCredentials.clientSecret, scopes: filteredCredentials.scopes });
      } else {
        await configApi.saveAdobeCredentials(filteredCredentials);
      }

      // Refresh configuration status from backend to get updated timestamp
      await loadSavedConfigurations();

      setAdobeModalOpen(false);
    } catch (error) {
      // Failed to save Adobe Commerce credentials
    }
  };

  const handleCustomersSave = async (type, config) => {
    try {
      // Persist dto + sourceSample as one payload under key 'customers'
      const payload = { dto: config.dto || {}, defaultSource: config.defaultSource || {} };
      const resp = await configApi.saveConfigByKey('customers', payload);
      setConfigStatus(prev => ({
        ...prev,
        customers: {
          hasConfig: true,
          lastUpdated: resp?.updatedAt || new Date().toISOString(),
          status: 'complete',
          mappings: config.mappings,
          sourceSample: config.sourceSample
        }
      }));
    } catch (e) {
      // Failed to save customers config
    } finally {
      setCustomersModalOpen(false);
    }
  };

  const handleItemsConfigSave = async (type, config) => {
    try {
      const payload = { dto: config.dto || {}, defaultSource: config.defaultSource || {} };
      const resp = await configApi.saveConfigByKey('products', payload);
      const updatedProducts = {
        ...configStatus.products,
        hasFieldMap: true,
        lastUpdated: resp?.updatedAt || new Date().toISOString(),
        itemsDto: config.dto,
        itemsSource: config.sourceSample
      };
      setConfigStatus(prev => ({
        ...prev,
        products: {
          ...updatedProducts,
          status: getProductsStatus(updatedProducts)
        }
      }));
    } catch (e) {
      // Failed to save items config
    } finally {
      setItemsConfigModalOpen(false);
    }
  };

  const handleProductSettingsSave = async (_type, settingsPayload) => {
    try {
      // Merge treatKitsAsInventory + bundleDynamicSku into the products DTO settings
      // so transformController reads them without an extra DB query
      const existing = await configApi.getConfigByKey('products');
      const existingDto = existing?.data?.dto?.fields
        ? existing.data.dto
        : (existing?.data?.dto?.dto || existing?.data?.dto || {});
      const existingSource = existing?.data?.defaultSource;
      const mergedDto = {
        ...existingDto,
        settings: {
          ...(existingDto.settings || {}),
          ...(settingsPayload || {})
        }
      };
      const resp = await configApi.saveConfigByKey('products', {
        dto: mergedDto,
        defaultSource: existingSource
      });
      setConfigStatus(prev => ({
        ...prev,
        products: {
          ...prev.products,
          hasProductSettings: true,
          lastUpdated: resp?.updatedAt || new Date().toISOString(),
          productSettings: { ...(settingsPayload || {}) },
          status: getProductsStatus(prev.products)
        }
      }));
    } catch (e) {
      // Failed to save product settings
    } finally {
      setProductSettingsModalOpen(false);
    }
  };

  const handleInventoryConfigSave = async (type, config) => {
    try {
      const payload = { dto: config.dto || {}, defaultSource: config.defaultSource || {} };
      const resp = await configApi.saveConfigByKey('inventory', payload);
      const updatedProducts = {
        ...configStatus.products,
        hasInventory: true,
        lastUpdated: resp?.updatedAt || new Date().toISOString(),
        inventoryDto: config.dto,
        inventorySource: config.sourceSample
      };
      setConfigStatus(prev => ({
        ...prev,
        products: {
          ...updatedProducts,
          status: getProductsStatus(updatedProducts)
        }
      }));
    } catch (e) {
      // Failed to save inventory config
    } finally {
      setInventoryConfigModalOpen(false);
    }
  };

  const handleProductsPriceLevelSave = (type, config) => {
    // Deprecated; handled by generic ConfigurationModal
  };

  const handlePriceLevelsConfigSave = async (type, config) => {
    try {
      const payload = { dto: config.dto || {}, defaultSource: config.defaultSource || {} };
      const resp = await configApi.saveConfigByKey('pricelevels', payload);
      const updatedProducts = {
        ...configStatus.products,
        hasPriceLevels: true,
        lastUpdated: resp?.updatedAt || new Date().toISOString(),
        priceLevelsDto: config.dto,
        priceLevelsSource: config.sourceSample
      };
      setConfigStatus(prev => ({
        ...prev,
        products: {
          ...updatedProducts,
          status: getProductsStatus(updatedProducts)
        }
      }));
    } catch (e) {
      // Failed to save price levels config
    } finally {
      setPriceLevelsConfigModalOpen(false);
    }
  };

  // Order save handlers

  const handleOrdersConfigSave = async (type, config) => {
    try {
      const payload = { dto: config.dto || {}, defaultSource: config.defaultSource || {} };
      const resp = await configApi.saveConfigByKey('orders', payload);
      setConfigStatus(prev => ({
        ...prev,
        orders: {
          ...prev.orders,
          hasConfig: true,
          dto: config.dto,
          sourceSample: config.sourceSample,
          lastUpdated: resp?.updatedAt || new Date().toISOString(),
        }
      }));
    } catch (e) {
      // Failed to save orders config
    } finally {
      setOrdersConfigModalOpen(false);
    }
  };

  const loadQboCustomerDepositsConfig = async () => {
    await keepAlive();
    try {
      const resp = await configApi.getConfigByKey('qbo_customer_deposits');
      if (resp && resp.success && (resp.configured || resp.data)) {
        const normalizedDto = resp.data?.dto?.fields ? resp.data.dto : (resp.data?.dto?.dto || null);
        setConfigStatus(prev => ({
          ...prev,
          qbo_customer_deposits: {
            ...prev.qbo_customer_deposits,
            hasConfig: !!normalizedDto || !!resp.configured,
            lastUpdated: resp.updatedAt || prev.qbo_customer_deposits?.lastUpdated || null,
            status: (normalizedDto || resp.configured) ? 'complete' : 'incomplete',
            dto: normalizedDto || prev.qbo_customer_deposits?.dto || null,
            sourceSample: resp.data?.defaultSource || prev.qbo_customer_deposits?.sourceSample || null
          }
        }));
      }
    } catch (e) {
      // Failed to load QBO customer deposits config
    }
  };

  const handleQboCustomerDepositsSave = async (type, config) => {
    try {
      const payload = {
        dto: config.dto || {},
        defaultSource: config.defaultSource || config.sourceSample || {},
        settingsDefaults: config.settingsDefaults || null
      };
      const resp = await configApi.saveConfigByKey('qbo_customer_deposits', payload);
      setConfigStatus(prev => ({
        ...prev,
        qbo_customer_deposits: {
          ...prev.qbo_customer_deposits,
          hasConfig: true,
          dto: config.dto,
          sourceSample: config.sourceSample || config.defaultSource || prev.qbo_customer_deposits?.sourceSample || null,
          lastUpdated: resp?.updatedAt || new Date().toISOString(),
          status: 'complete'
        }
      }));
    } catch (e) {
      // Failed to save QBO customer deposits config
    } finally {
      setQboCustomerDepositsModalOpen(false);
    }
  };

  const loadQboShipmentInvoicesConfig = async () => {
    await keepAlive();
    try {
      const resp = await configApi.getConfigByKey('qbo_shipment_invoices');
      if (resp && resp.success && (resp.configured || resp.data)) {
        const normalizedDto = resp.data?.dto?.fields ? resp.data.dto : (resp.data?.dto?.dto || null);
        setConfigStatus(prev => ({
          ...prev,
          qbo_shipment_invoices: {
            ...prev.qbo_shipment_invoices,
            hasConfig: !!normalizedDto || !!resp.configured,
            lastUpdated: resp.updatedAt || prev.qbo_shipment_invoices?.lastUpdated || null,
            status: (normalizedDto || resp.configured) ? 'complete' : 'incomplete',
            dto: normalizedDto || prev.qbo_shipment_invoices?.dto || null,
            sourceSample: resp.data?.defaultSource || prev.qbo_shipment_invoices?.sourceSample || null
          }
        }));
      }
    } catch (e) {
      // Failed to load QBO shipment invoices config
    }
  };

  const handleQboShipmentInvoicesSave = async (type, config) => {
    try {
      const payload = {
        dto: config.dto || {},
        defaultSource: config.defaultSource || config.sourceSample || {},
        settingsDefaults: config.settingsDefaults || null
      };
      const resp = await configApi.saveConfigByKey('qbo_shipment_invoices', payload);
      setConfigStatus(prev => ({
        ...prev,
        qbo_shipment_invoices: {
          ...prev.qbo_shipment_invoices,
          hasConfig: true,
          dto: config.dto,
          sourceSample: config.sourceSample || config.defaultSource || prev.qbo_shipment_invoices?.sourceSample || null,
          lastUpdated: resp?.updatedAt || new Date().toISOString(),
          status: 'complete'
        }
      }));
    } catch (e) {
      // Failed to save QBO shipment invoices config
    } finally {
      setQboShipmentInvoicesModalOpen(false);
    }
  };

  const loadQboRefundsConfig = async () => {
    await keepAlive();
    try {
      const resp = await configApi.getConfigByKey('qbo_refunds');
      if (resp && resp.success && (resp.configured || resp.data)) {
        const normalizedDto = resp.data?.dto?.fields ? resp.data.dto : (resp.data?.dto?.dto || null);
        setConfigStatus(prev => ({
          ...prev,
          qbo_refunds: {
            ...prev.qbo_refunds,
            hasConfig: !!normalizedDto || !!resp.configured,
            lastUpdated: resp.updatedAt || prev.qbo_refunds?.lastUpdated || null,
            status: (normalizedDto || resp.configured) ? 'complete' : 'incomplete',
            dto: normalizedDto || prev.qbo_refunds?.dto || null,
            sourceSample: resp.data?.defaultSource || prev.qbo_refunds?.sourceSample || null
          }
        }));
      }
    } catch (e) {
      // Failed to load QBO refunds config
    }
  };

  const handleQboRefundsSave = async (type, config) => {
    try {
      const payload = {
        dto: config.dto || {},
        defaultSource: config.defaultSource || config.sourceSample || {},
        settingsDefaults: config.settingsDefaults || null
      };
      const resp = await configApi.saveConfigByKey('qbo_refunds', payload);
      setConfigStatus(prev => ({
        ...prev,
        qbo_refunds: {
          ...prev.qbo_refunds,
          hasConfig: true,
          dto: config.dto,
          sourceSample: config.sourceSample || config.defaultSource || prev.qbo_refunds?.sourceSample || null,
          lastUpdated: resp?.updatedAt || new Date().toISOString(),
          status: 'complete'
        }
      }));
    } catch (e) {
      // Failed to save QBO refunds config
    } finally {
      setQboRefundsModalOpen(false);
    }
  };

  const loadQboFeeJournalsConfig = async () => {
    await keepAlive();
    try {
      const resp = await configApi.getConfigByKey('qbo_fee_journals');
      if (resp && resp.success && (resp.configured || resp.data)) {
        const normalizedDto = resp.data?.dto?.fields ? resp.data.dto : (resp.data?.dto?.dto || null);
        setConfigStatus(prev => ({
          ...prev,
          qbo_fee_journals: {
            ...prev.qbo_fee_journals,
            hasConfig: !!normalizedDto || !!resp.configured,
            lastUpdated: resp.updatedAt || prev.qbo_fee_journals?.lastUpdated || null,
            status: (normalizedDto || resp.configured) ? 'complete' : 'incomplete',
            dto: normalizedDto || prev.qbo_fee_journals?.dto || null,
            sourceSample: resp.data?.defaultSource || prev.qbo_fee_journals?.sourceSample || null
          }
        }));
      }
    } catch (e) {
      // Failed to load QBO fee journals config
    }
  };

  const handleQboFeeJournalsSave = async (type, config) => {
    try {
      const payload = {
        dto: config.dto || {},
        defaultSource: config.defaultSource || config.sourceSample || {},
        settingsDefaults: config.settingsDefaults || null
      };
      const resp = await configApi.saveConfigByKey('qbo_fee_journals', payload);
      setConfigStatus(prev => ({
        ...prev,
        qbo_fee_journals: {
          ...prev.qbo_fee_journals,
          hasConfig: true,
          dto: config.dto,
          sourceSample: config.sourceSample || config.defaultSource || prev.qbo_fee_journals?.sourceSample || null,
          lastUpdated: resp?.updatedAt || new Date().toISOString(),
          status: 'complete'
        }
      }));
    } catch (e) {
      // Failed to save QBO fee journals config
    } finally {
      setQboFeeJournalsModalOpen(false);
    }
  };

  const loadQboVendorBillsConfig = async () => {
    await keepAlive();
    try {
      const resp = await configApi.getConfigByKey('qbo_vendor_bills');
      if (resp && resp.success && (resp.configured || resp.data)) {
        const normalizedDto = resp.data?.dto?.fields ? resp.data.dto : (resp.data?.dto?.dto || null);
        setConfigStatus(prev => ({
          ...prev,
          qbo_vendor_bills: {
            ...prev.qbo_vendor_bills,
            hasConfig: !!normalizedDto || !!resp.configured,
            lastUpdated: resp.updatedAt || prev.qbo_vendor_bills?.lastUpdated || null,
            status: (normalizedDto || resp.configured) ? 'complete' : 'incomplete',
            dto: normalizedDto || prev.qbo_vendor_bills?.dto || null,
            sourceSample: resp.data?.defaultSource || prev.qbo_vendor_bills?.sourceSample || null
          }
        }));
      }
    } catch (e) {
      // Failed to load QBO vendor bills config
    }
  };

  const handleQboVendorBillsSave = async (type, config) => {
    try {
      const payload = {
        dto: config.dto || {},
        defaultSource: config.defaultSource || config.sourceSample || {},
        settingsDefaults: config.settingsDefaults || null
      };
      const resp = await configApi.saveConfigByKey('qbo_vendor_bills', payload);
      setConfigStatus(prev => ({
        ...prev,
        qbo_vendor_bills: {
          ...prev.qbo_vendor_bills,
          hasConfig: true,
          dto: config.dto,
          sourceSample: config.sourceSample || config.defaultSource || prev.qbo_vendor_bills?.sourceSample || null,
          lastUpdated: resp?.updatedAt || new Date().toISOString(),
          status: 'complete'
        }
      }));
    } catch (e) {
      // Failed to save QBO vendor bills config
    } finally {
      setQboVendorBillsModalOpen(false);
    }
  };

  // Removed handleOrdersGeneralSave; orders use generic ConfigurationModal



  const handleOrdersSettingsSave = async (_type, settingsPayload) => {
    try {
      // Load current orders DTO
      const resp = await configApi.getConfigByKey('orders');
      const data = resp?.data || {};
      const dto = data.dto?.fields ? data.dto : (data.dto?.dto || null);

      if (!dto) {
        // Orders DTO not found
        return;
      }

      // Update order settings in DTO
      const updatedDto = {
        ...dto,
        settings: {
          ...dto.settings,
          order: {
            ...dto.settings?.order,
            ...settingsPayload.order
          },
          // Prepayment: modal writes order.prepayment; DTO/transform use settings.prepayment
          ...(settingsPayload.order?.prepayment && {
            prepayment: {
              ...dto.settings?.prepayment,
              ...settingsPayload.order.prepayment
            }
          })
        }
      };

      // Save updated DTO back, preserving all fields like other save handlers
      const payload = {
        dto: updatedDto,
        defaultSource: data.defaultSource || null,
        settingsDefaults: data.settingsDefaults || null
      };
      const saveResp = await configApi.saveConfigByKey('orders', payload);
      setConfigStatus(prev => ({
        ...prev,
        orders: {
          ...prev.orders,
          hasConfig: true,
          lastUpdated: saveResp?.updatedAt || new Date().toISOString(),
          dto: updatedDto  // Store DTO for destination lookup
        }
      }));
    } catch (e) {
      // Failed to save orders settings
    } finally {
      setOrdersSettingsModalOpen(false);
    }
  };

  const loadCashSalesSettings = async () => {
    await keepAlive();
    try {
      const resp = await configApi.getConfigByKey('cash_sales');
      const data = resp?.data || {};
      const dto = data.dto?.fields ? data.dto : (data.dto?.dto || null);
      const settings = dto?.settings || {};
      const orderSettings = settings.order || {};

      setCashSalesSettingsState({ order: orderSettings });
      if (dto) {
        setConfigStatus(prev => ({
          ...prev,
          cash_sales: {
            ...prev.cash_sales,
            hasSavedSearch: true,
            dto,
            sourceSample: data.defaultSource || prev.cash_sales?.sourceSample,
            lastUpdated: resp.updatedAt || prev.cash_sales?.lastUpdated || null,
            status: 'complete'
          }
        }));
      }
    } catch (e) {
      // Failed to load cash sales settings
      setCashSalesSettingsState({ order: {} });
    } finally {
      setCashSalesSettingsModalOpen(true);
    }
  };

  const handleCashSalesSettingsSave = async (_type, settingsPayload) => {
    try {
      const resp = await configApi.getConfigByKey('cash_sales');
      const data = resp?.data || {};
      const dto = data.dto?.fields ? data.dto : (data.dto?.dto || null);

      if (!dto) {
        return;
      }

      const normalizedOrderSettings = { ...(settingsPayload.order || {}) };
      if (normalizedOrderSettings.orderCreationType) {
        normalizedOrderSettings.posOrderCreationType = normalizedOrderSettings.orderCreationType;
      }

      const updatedFields = Array.isArray(dto.fields)
        ? dto.fields.map((field) => {
            if (field.id !== 'orderCreationType' && field.destPath !== 'orderCreationType') return field;
            return {
              ...field,
              mapping: {
                ...(field.mapping || {}),
                type: 'static',
                value: normalizedOrderSettings.orderCreationType || 'cash_sale',
                policy: field.mapping?.policy || 'create'
              }
            };
          })
        : dto.fields;

      const updatedDto = {
        ...dto,
        fields: updatedFields,
        settings: {
          ...dto.settings,
          order: {
            ...dto.settings?.order,
            ...normalizedOrderSettings
          }
        }
      };

      const payload = {
        dto: updatedDto,
        defaultSource: data.defaultSource || null,
        settingsDefaults: data.settingsDefaults || null
      };
      const saveResp = await configApi.saveConfigByKey('cash_sales', payload);
      setConfigStatus(prev => ({
        ...prev,
        cash_sales: {
          ...prev.cash_sales,
          hasSavedSearch: true,
          lastUpdated: saveResp?.updatedAt || new Date().toISOString(),
          dto: updatedDto,
          sourceSample: data.defaultSource || prev.cash_sales?.sourceSample,
          status: 'complete'
        }
      }));
    } catch (e) {
      // Failed to save cash sales settings
    } finally {
      setCashSalesSettingsModalOpen(false);
    }
  };

  const handleOrdersPaymentSave = (type, config) => {
    const updatedOrders = {
      ...configStatus.orders,
      hasPayment: true,
      lastUpdated: new Date().toISOString(),
    };

    setConfigStatus(prev => ({
      ...prev,
      orders: {
        ...updatedOrders,
        status: getOrdersStatus(updatedOrders)
      }
    }));
    // removed
  };

  const handleOrdersShippingSave = (type, config) => {
    const updatedOrders = {
      ...configStatus.orders,
      hasShipping: true,
      lastUpdated: new Date().toISOString(),
    };

    setConfigStatus(prev => ({
      ...prev,
      orders: {
        ...updatedOrders,
        status: getOrdersStatus(updatedOrders)
      }
    }));
    // removed
  };

  const handleOrderUpdatesSave = async (type, config) => {
    try {
      const payload = { dto: config.dto || {}, defaultSource: config.defaultSource || {} };
      const resp = await configApi.saveConfigByKey('order_updates', payload);
      setConfigStatus(prev => ({
        ...prev,
        order_updates: {
          ...prev.order_updates,
          hasConfig: true,
          dto: config.dto,
          sourceSample: config.defaultSource || config.sourceSample || prev.order_updates?.sourceSample,
          lastUpdated: resp?.updatedAt || new Date().toISOString(),
          status: 'complete'
        }
      }));
    } catch (e) {
      // Failed to save order update config
    } finally {
      setOrderUpdatesModalOpen(false);
    }
  };

  // Images disabled for now

  const handleFulfillmentSave = async (type, config) => {
    try {
      const payload = { dto: config.dto || {}, defaultSource: config.defaultSource || {} };
      const primaryKey = fulfillmentModalKey || 'fulfillments';
      const fallbackKey = primaryKey === 'fulfillments' ? 'fulfillment' : null;
      let resp = await configApi.saveConfigByKey(primaryKey, payload);
      if (!(resp && resp.success)) {
        if (!fallbackKey) throw new Error(resp?.error || `Failed to save ${primaryKey} configuration`);
        resp = await configApi.saveConfigByKey(fallbackKey, payload);
      }
      const updatedFulfillment = {
        ...(configStatus[primaryKey] || configStatus.fulfillment),
        hasSavedSearch: true,
        dto: config.dto,
        sourceSample: config.sourceSample,
        lastUpdated: resp?.updatedAt || new Date().toISOString(),
      };
      setConfigStatus(prev => ({
        ...prev,
        [primaryKey]: {
          ...updatedFulfillment,
          status: getFulfillmentStatus(updatedFulfillment)
        },
        fulfillment: {
          ...prev.fulfillment,
          hasSavedSearch: true,
          lastUpdated: updatedFulfillment.lastUpdated,
          status: getFulfillmentStatus({ ...prev.fulfillment, hasSavedSearch: true })
        }
      }));
    } catch (e) {
      // Failed to save fulfillment config
    } finally {
      setFulfillmentModalOpen(false);
    }
  };

  const openFulfillmentConfig = async (configKey) => {
    await keepAlive();
    setFulfillmentModalKey(configKey);
    try {
      let resp = await configApi.getConfigByKey(configKey);
      if (!(resp && resp.success && resp.configured) && configKey === 'fulfillments') {
        resp = await configApi.getConfigByKey('fulfillment');
      }
      if (resp && resp.success && resp.configured) {
        const normalizedDto = resp.data?.dto?.fields ? resp.data.dto : (resp.data?.dto?.dto || null);
        setConfigStatus(prev => ({
          ...prev,
          [configKey]: {
            ...(prev[configKey] || prev.fulfillment),
            hasSavedSearch: true,
            lastUpdated: resp.updatedAt || null,
            status: 'complete',
            dto: normalizedDto || prev[configKey]?.dto || prev.fulfillment?.dto,
            sourceSample: resp.data?.defaultSource || prev[configKey]?.sourceSample || prev.fulfillment?.sourceSample
          },
          fulfillment: {
            ...prev.fulfillment,
            hasSavedSearch: true,
            lastUpdated: resp.updatedAt || prev.fulfillment?.lastUpdated || null,
            status: 'complete'
          }
        }));
      }
    } catch (e) {
      // Failed to load fulfillment config
    } finally {
      setFulfillmentModalOpen(true);
    }
  };

  const handleImagesSave = async (type, config) => {
    try {
      // Save images configuration (DTO + sample), mirroring other DTO saves
      const payload = { dto: config.dto || {}, defaultSource: config.defaultSource || {} };
      const resp = await configApi.saveConfigByKey('images', payload);
      setConfigStatus(prev => ({
        ...prev,
        images: {
          hasSavedSearch: true,
          lastUpdated: resp?.updatedAt || new Date().toISOString(),
          status: 'complete'
        }
      }));
    } catch (e) {
      // Failed to save images config
    } finally {
      setImagesModalOpen(false);
    }
  };

  const handleReturnsSave = async (type, config) => {
    try {
      const payload = { dto: config.dto || {}, defaultSource: config.defaultSource || {} };
      const resp = await configApi.saveConfigByKey(returnsConfigKey, payload);
      const updatedReturns = {
        ...activeReturnsStatus,
        hasSavedSearch: true,
        dto: config.dto,
        sourceSample: config.sourceSample,
        lastUpdated: resp?.updatedAt || new Date().toISOString(),
      };
      setConfigStatus(prev => ({
        ...prev,
        [returnsConfigKey]: {
          ...updatedReturns,
          status: 'complete'
        }
      }));
    } catch (e) {
      // Failed to save returns config
    } finally {
      setReturnsModalOpen(false);
    }
  };

  const handleRefundsSave = async (type, config) => {
    try {
      const payload = { dto: config.dto || {}, defaultSource: config.defaultSource || {} };
      const resp = await configApi.saveConfigByKey('refunds', payload);
      const updatedRefunds = {
        ...configStatus.refunds,
        hasSavedSearch: true,
        dto: config.dto,
        sourceSample: config.sourceSample,
        lastUpdated: resp?.updatedAt || new Date().toISOString(),
      };
      setConfigStatus(prev => ({
        ...prev,
        refunds: {
          ...updatedRefunds,
          status: 'complete'
        }
      }));
    } catch (e) {
      // Failed to save refunds config
    } finally {
      setRefundsModalOpen(false);
    }
  };

  const loadServiceConfig = async (serviceKey, setOpen) => {
    await keepAlive();
    try {
      const resp = await configApi.getConfigByKey(serviceKey);
      if (resp && resp.success && resp.configured) {
        const normalizedDto = resp.data?.dto?.fields ? resp.data.dto : (resp.data?.dto?.dto || null);
        setConfigStatus(prev => ({
          ...prev,
          [serviceKey]: {
            ...prev[serviceKey],
            hasSavedSearch: true,
            lastUpdated: resp.updatedAt || null,
            status: 'complete',
            dto: normalizedDto || prev[serviceKey]?.dto,
            sourceSample: resp.data?.defaultSource || prev[serviceKey]?.sourceSample
          }
        }));
      }
    } catch (e) {
      // Failed to load service config
    } finally {
      setOpen(true);
    }
  };

  const saveServiceConfig = async (serviceKey, setOpen, config) => {
    try {
      const payload = { dto: config.dto || {}, defaultSource: config.defaultSource || {} };
      const resp = await configApi.saveConfigByKey(serviceKey, payload);
      const updated = {
        ...configStatus[serviceKey],
        hasSavedSearch: true,
        dto: config.dto,
        sourceSample: config.sourceSample,
        lastUpdated: resp?.updatedAt || new Date().toISOString(),
      };
      setConfigStatus(prev => ({
        ...prev,
        [serviceKey]: {
          ...updated,
          status: 'complete'
        }
      }));
    } catch (e) {
      // Failed to save service config
    } finally {
      setOpen(false);
    }
  };

  const handleCashSalesSave = (type, config) => saveServiceConfig('cash_sales', setCashSalesModalOpen, config);
  const handleCashRefundsSave = (type, config) => saveServiceConfig('cash_refunds', setCashRefundsModalOpen, config);
  const handleExchangesSave = (type, config) => saveServiceConfig('exchanges', setExchangesModalOpen, config);
  const handlePayoutsSave = (type, config) => saveServiceConfig('payouts', setPayoutsModalOpen, config);
  const handlePayoutSettlementsSave = (type, config) => saveServiceConfig('payout_settlements', setPayoutSettlementsModalOpen, config);

  const loadPayoutSettings = async () => {
    await keepAlive();
    try {
      const resp = await configApi.getConfigByKey('payouts');
      const data = resp?.data || {};
      const dto = data.dto?.fields ? data.dto : (data.dto?.dto || null);
      const payoutSettings = dto?.settings?.payout || {};

      setPayoutSettingsState({ payout: payoutSettings });
      if (dto) {
        setConfigStatus(prev => ({
          ...prev,
          payouts: {
            ...prev.payouts,
            hasSavedSearch: true,
            dto,
            sourceSample: data.defaultSource || prev.payouts?.sourceSample,
            lastUpdated: resp.updatedAt || prev.payouts?.lastUpdated || null,
            status: 'complete'
          }
        }));
      }
    } catch (e) {
      setPayoutSettingsState({ payout: {} });
    } finally {
      setPayoutSettingsModalOpen(true);
    }
  };

  const handlePayoutSettingsSave = async (_type, settingsPayload) => {
    try {
      const resp = await configApi.getConfigByKey('payouts');
      const data = resp?.data || {};
      const dto = data.dto?.fields ? data.dto : (data.dto?.dto || null);
      if (!dto) return;
      const currentPayoutSettings = { ...(dto.settings?.payout || {}) };
      delete currentPayoutSettings.invoiceLookup;
      delete currentPayoutSettings.invoiceLookupUrl;
      delete currentPayoutSettings.invoiceLookupScript;
      delete currentPayoutSettings.invoiceLookupDeploy;
      delete currentPayoutSettings.invoiceLookupAction;
      delete currentPayoutSettings.invoiceLookupMethod;

      const updatedDto = {
        ...dto,
        settings: {
          ...dto.settings,
          payout: {
            ...currentPayoutSettings,
            ...(settingsPayload.payout || {})
          }
        }
      };

      const saveResp = await configApi.saveConfigByKey('payouts', {
        dto: updatedDto,
        defaultSource: data.defaultSource || null,
        settingsDefaults: data.settingsDefaults || null
      });
      setConfigStatus(prev => ({
        ...prev,
        payouts: {
          ...prev.payouts,
          hasSavedSearch: true,
          dto: updatedDto,
          sourceSample: data.defaultSource || prev.payouts?.sourceSample,
          lastUpdated: saveResp?.updatedAt || new Date().toISOString(),
          status: 'complete'
        }
      }));
    } catch (e) {
      // Failed to save payout settings
    } finally {
      setPayoutSettingsModalOpen(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'complete':
        return <Badge tone="success">Complete</Badge>;
      case 'incomplete':
        return <Badge tone="attention">Incomplete</Badge>;
      case 'error':
        return <Badge tone="critical">Error</Badge>;
      default:
        return <Badge tone="attention">Incomplete</Badge>;
    }
  };

  const getLastUpdatedText = (lastUpdated) => {
    if (!lastUpdated) return null;
    const formatted = formatUtcToTz(lastUpdated, tz);
    return `Updated : ${formatted}`;
  };

  const handleAdobeModalOpen = async () => {
    await keepAlive();
    if (configStatus.adobe.hasCredentials) {
      try {
        const response = await configApi.getCredentials('adobe');
        if (response.success) {
          setAdobeCredentials(response.credentials);
        }
      } catch (error) {
        // Error loading Adobe credentials
      }
    }
    setAdobeModalOpen(true);
  };

  const handleShopifyModalOpen = async () => {
    await keepAlive();
    if (configStatus.shopify?.hasCredentials) {
      try {
        const response = await configApi.getShopifyConfig();
        if (response.success && response.config) {
          setShopifyCredentials(response.config);
        } else {
          setShopifyCredentials({});
        }
      } catch (error) {
        // Error loading Shopify config
        setShopifyCredentials({});
      }
    } else {
      setShopifyCredentials({});
    }
    setShopifyModalOpen(true);
  };

  const handleSageModalOpen = async () => {
    await keepAlive();
    try {
      const response = await configApi.getSageConfig();
      if (response.success && response.config) {
        setSageCredentials(response.config);
      } else {
        setSageCredentials({});
      }
    } catch (error) {
      // Error loading SAGE config
      setSageCredentials({});
    }
    setSageModalOpen(true);
  };

  const handleKdsModalOpen = async () => {
    await keepAlive();
    try {
      const response = await configApi.getCredentials('kds');
      if (response.success && response.credentials) {
        setKdsCredentials(response.credentials);
      } else {
        setKdsCredentials({});
      }
    } catch (error) {
      setKdsCredentials({});
    }
    setKdsModalOpen(true);
  };

  const handleWayfairModalOpen = async () => {
    await keepAlive();
    try {
      const response = await configApi.getCredentials('wayfair');
      if (response.success && response.credentials) {
        setWayfairCredentials(response.credentials);
      } else {
        setWayfairCredentials({});
      }
    } catch (error) {
      setWayfairCredentials({});
    }
    setWayfairModalOpen(true);
  };

  const handleWayfairAgentModalOpen = async () => {
    await keepAlive();
    try {
      const response = await configApi.getCredentials('wayfairagent');
      if (response.success && response.credentials) {
        setWayfairAgentCredentials(response.credentials);
      } else {
        setWayfairAgentCredentials({});
      }
    } catch (error) {
      setWayfairAgentCredentials({});
    }
    setWayfairAgentModalOpen(true);
  };

  const handleQuickBooksModalOpen = async () => {
    await keepAlive();
    try {
      const response = await configApi.getCredentials('quickbooks');
      if (response.success && response.credentials) {
        setQuickBooksCredentials(response.credentials);
      } else {
        setQuickBooksCredentials({});
      }
    } catch (error) {
      setQuickBooksCredentials({});
    }
    setQuickBooksModalOpen(true);
  };

  const handleShopifySave = async (payload) => {
    try {
      await keepAlive();
      // payload is undefined when called after OAuth redirect — skip save, just refresh
      if (payload) {
        await configApi.saveShopifyConfig(payload);
      }
      await loadSavedConfigurations();
      setShopifyModalOpen(false);
    } catch (error) {
      // Error saving Shopify config
    }
  };

  const handleSageSave = async (payload) => {
    try {
      await keepAlive();
      await configApi.saveSageConfig(payload);
      await loadSavedConfigurations();
      setSageModalOpen(false);
    } catch (error) {
      // Error saving SAGE config
    }
  };

  const handleKdsSave = async () => {
    await keepAlive();
    await loadSavedConfigurations();
    setKdsModalOpen(false);
  };

  const handleWayfairSave = async () => {
    await keepAlive();
    await loadSavedConfigurations();
    setWayfairModalOpen(false);
  };

  const handleWayfairAgentSave = async () => {
    await keepAlive();
    await loadSavedConfigurations();
    setWayfairAgentModalOpen(false);
  };

  const handleQuickBooksSave = async () => {
    await keepAlive();
    await loadSavedConfigurations();
    setQuickBooksModalOpen(false);
  };

  return (
    <Page title="Configuration">
      <Layout>


        {/* 20px spacing div */}
        <div style={{ height: '20px' }}></div>
        {/* Credentials Configuration */}
        <Layout.Section>
          <Card>
            <BlockStack gap="loose">
              <div>
                <Text variant="headingMd" fontWeight="bold" as="h2">
                  Secure Connections
                </Text>
                <Text variant="bodyMd" as="p" color="subdued">
                  Configure secure connections for your integrations
                </Text>
              </div>

              <div className="action-buttons">
                {/* Show Adobe Commerce only if it's the frontend or backend provider */}
                {(currentWorkspace?.integrations?.frontend?.provider === 'adobe' ||
                  currentWorkspace?.integrations?.backend?.provider === 'adobe') && (
                  <div className="config-item">
                    <Button
                      size="large"
                      onClick={handleAdobeModalOpen}
                      variant="secondary"
                      fullWidth={false}
                      style={{ width: '100%' }}
                    >
                      <Text variant="headingSm" fontWeight="bold" color="subdued">
                        Adobe Commerce {configStatus.adobe.hasCredentials ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                      </Text>
                    </Button>
                    {configStatus.adobe.hasCredentials && (
                      <div className="config-status">
                        <Badge tone="success">Configured</Badge>
                        {configStatus.adobe.lastUpdated && (
                          <Text variant="bodySm" color="subdued">
                            {getLastUpdatedText(configStatus.adobe.lastUpdated)}
                          </Text>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Show Shopify only if it's the frontend or backend provider */}
                {(currentWorkspace?.integrations?.frontend?.provider === 'shopify' ||
                  currentWorkspace?.integrations?.backend?.provider === 'shopify') && (
                  <div className="config-item">
                    <Button
                      size="large"
                      onClick={handleShopifyModalOpen}
                      variant="secondary"
                      fullWidth={false}
                      style={{ width: '100%' }}
                    >
                      <Text variant="headingSm" fontWeight="bold" color="subdued">
                        Shopify {configStatus.shopify?.hasCredentials ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                      </Text>
                    </Button>
                    {configStatus.shopify?.hasCredentials && (
                      <div className="config-status">
                        <Badge tone="success">Configured</Badge>
                        {configStatus.shopify.lastUpdated && (
                          <Text variant="bodySm" color="subdued">
                            {getLastUpdatedText(configStatus.shopify.lastUpdated)}
                          </Text>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Show Wayfair only if it's the frontend or backend provider */}
                {(currentWorkspace?.integrations?.frontend?.provider === 'wayfair' ||
                  currentWorkspace?.integrations?.backend?.provider === 'wayfair') && (
                  <div className="config-item">
                    <Button
                      size="large"
                      onClick={handleWayfairModalOpen}
                      variant="secondary"
                      fullWidth={false}
                      style={{ width: '100%' }}
                    >
                      <Text variant="headingSm" fontWeight="bold" color="subdued">
                        Wayfair {configStatus.wayfair?.hasCredentials ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                      </Text>
                    </Button>
                    {configStatus.wayfair?.hasCredentials && (
                      <div className="config-status">
                        <Badge tone="success">Configured</Badge>
                        {configStatus.wayfair.lastUpdated && (
                          <Text variant="bodySm" color="subdued">
                            {getLastUpdatedText(configStatus.wayfair.lastUpdated)}
                          </Text>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Show Wayfair Agent only if it's the frontend or backend provider */}
                {(currentWorkspace?.integrations?.frontend?.provider === 'wayfairagent' ||
                  currentWorkspace?.integrations?.backend?.provider === 'wayfairagent') && (
                  <div className="config-item">
                    <Button
                      size="large"
                      onClick={handleWayfairAgentModalOpen}
                      variant="secondary"
                      fullWidth={false}
                      style={{ width: '100%' }}
                    >
                      <Text variant="headingSm" fontWeight="bold" color="subdued">
                        Wayfair Agent {configStatus.wayfairagent?.hasCredentials ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                      </Text>
                    </Button>
                    {configStatus.wayfairagent?.hasCredentials && (
                      <div className="config-status">
                        <Badge tone="success">Configured</Badge>
                        {configStatus.wayfairagent.lastUpdated && (
                          <Text variant="bodySm" color="subdued">
                            {getLastUpdatedText(configStatus.wayfairagent.lastUpdated)}
                          </Text>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Show QuickBooks only if it's the frontend or backend provider */}
                {(currentWorkspace?.integrations?.frontend?.provider === 'quickbooks' ||
                  currentWorkspace?.integrations?.backend?.provider === 'quickbooks') && (
                  <div className="config-item">
                    <Button
                      size="large"
                      onClick={handleQuickBooksModalOpen}
                      variant="secondary"
                      fullWidth={false}
                      style={{ width: '100%' }}
                    >
                      <Text variant="headingSm" fontWeight="bold" color="subdued">
                        QuickBooks {configStatus.quickbooks?.hasCredentials ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                      </Text>
                    </Button>
                    {configStatus.quickbooks?.hasCredentials && (
                      <div className="config-status">
                        <Badge tone="success">Configured</Badge>
                        {configStatus.quickbooks.lastUpdated && (
                          <Text variant="bodySm" color="subdued">
                            {getLastUpdatedText(configStatus.quickbooks.lastUpdated)}
                          </Text>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Show NetSuite M2M only if NetSuite is a provider */}
                {(currentWorkspace?.integrations?.frontend?.provider === 'netsuite' ||
                  currentWorkspace?.integrations?.backend?.provider === 'netsuite') && (
                  <div className="config-item">
                    <Button size="large" onClick={async ()=>{
                      await keepAlive();
                      try {
                        const resp = await configApi.getCredentials('netsuite');
                        if (resp && resp.success && resp.credentials) {
                          setNetsuiteM2MConfig(resp.credentials);
                        } else {
                          setNetsuiteM2MConfig({});
                        }
                      } catch (_) {
                        setNetsuiteM2MConfig({});
                      } finally {
                        setNetsuiteM2MOpen(true);
                      }
                    }} variant="secondary" fullWidth={false} style={{ width: '100%' }}>
                      <Text variant="headingSm" fontWeight="bold" color="subdued">NetSuite M2M {configStatus.netsuite.hasCredentials ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}</Text>
                    </Button>
                    {configStatus.netsuite.hasCredentials && (
                      <div className="config-status">
                        <Badge tone="success">Configured</Badge>
                        {configStatus.netsuite.lastUpdated && (
                          <Text variant="bodySm" color="subdued">
                            {getLastUpdatedText(configStatus.netsuite.lastUpdated)}
                          </Text>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Show SAGE only if it's the frontend or backend provider */}
                {(currentWorkspace?.integrations?.frontend?.provider === 'sage' ||
                  currentWorkspace?.integrations?.backend?.provider === 'sage') && (
                  <div className="config-item">
                    <Button
                      size="large"
                      onClick={handleSageModalOpen}
                      variant="secondary"
                      fullWidth={false}
                      style={{ width: '100%' }}
                    >
                      <Text variant="headingSm" fontWeight="bold" color="subdued">
                        SAGE 300 {configStatus.sage?.hasCredentials ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                      </Text>
                    </Button>
                    {configStatus.sage?.hasCredentials && (
                      <div className="config-status">
                        <Badge tone="success">Configured</Badge>
                        {configStatus.sage.lastUpdated && (
                          <Text variant="bodySm" color="subdued">
                            {getLastUpdatedText(configStatus.sage.lastUpdated)}
                          </Text>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Show generic bearer-token connection when KDS is a provider */}
                {(currentWorkspace?.integrations?.frontend?.provider === 'kds' ||
                  currentWorkspace?.integrations?.backend?.provider === 'kds') && (
                  <div className="config-item">
                    <Button
                      size="large"
                      onClick={handleKdsModalOpen}
                      variant="secondary"
                      fullWidth={false}
                      style={{ width: '100%' }}
                    >
                      <Text variant="headingSm" fontWeight="bold" color="subdued">
                        KDS {configStatus.kds?.hasCredentials ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                      </Text>
                    </Button>
                    {configStatus.kds?.hasCredentials && (
                      <div className="config-status">
                        <Badge tone="success">Configured</Badge>
                        {configStatus.kds.lastUpdated && (
                          <Text variant="bodySm" color="subdued">
                            {getLastUpdatedText(configStatus.kds.lastUpdated)}
                          </Text>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* 20px spacing div */}
        <div style={{ height: '20px' }}></div>

        {/* Product Configuration */}
        <Layout.Section>
          <Card>
            <BlockStack gap="loose">
              <div>
                <Text variant="headingMd" fontWeight="bold" as="h2">
                  Product Configuration
                </Text>
                <Text variant="bodyMd" as="p" color="subdued">
                  Configure product field mappings, inventory locations, and price levels
                </Text>
              </div>

              <div className="action-buttons-4col">
                {serviceGates.products && (
                  <Button
                    size="large"
                    variant="secondary"
                    fullWidth={false}
                    disabled={!gatesReady}
                    onClick={async () => {
                      keepAlive();
                      try {
                        const resp = await configApi.getConfigByKey('products');
                        if (resp && resp.success && resp.configured) {
                          const normalizedDto = resp.data?.dto?.fields ? resp.data.dto : (resp.data?.dto?.dto || null);
                          setConfigStatus(prev => ({
                            ...prev,
                            products: {
                              ...prev.products,
                              hasFieldMap: true,
                              lastUpdated: resp.updatedAt || null,
                              itemsDto: normalizedDto || prev.products?.itemsDto,
                              itemsSource: resp.data?.defaultSource || prev.products?.itemsSource
                            }
                          }));
                        }
                      } catch (e) {
                        // Failed to load items config
                      } finally {
                        setItemsConfigModalOpen(true);
                      }
                    }}
                  >
                    <Text variant="headingSm" fontWeight="bold" color="subdued">
                      Products configuration {configStatus.products.hasFieldMap ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                    </Text>
                  </Button>
                )}

                {serviceGates.products && (
                  <Button
                    size="large"
                    variant="secondary"
                    fullWidth={false}
                    disabled={!gatesReady}
                  onClick={async () => {
                    keepAlive();
                    try {
                      const resp = await configApi.getConfigByKey('products');
                      const dtoSettings = resp?.data?.dto?.settings || resp?.data?.dto?.dto?.settings || {};
                      setConfigStatus(prev => ({
                        ...prev,
                        products: {
                          ...prev.products,
                          lastUpdated: resp?.updatedAt || prev.products?.lastUpdated,
                          productSettings: {
                            treatKitsAsInventory: dtoSettings.treatKitsAsInventory !== undefined
                              ? !!dtoSettings.treatKitsAsInventory
                              : true,
                            bundleDynamicSku: !!dtoSettings.bundleDynamicSku,
                          }
                        }
                      }));
                    } catch (e) {
                      // ignore; open modal with defaults
                    } finally {
                      setProductSettingsModalOpen(true);
                    }
                  }}
                >
                  <Text variant="headingSm" fontWeight="bold" color="subdued">
                    Product Settings {configStatus.products.hasProductSettings ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                  </Text>
                </Button>
                )}

                {serviceGates.inventory && (
                  <Button
                    size="large"
                    variant="secondary"
                    fullWidth={false}
                    disabled={!gatesReady}
                  onClick={async () => {
                    keepAlive();
                    try {
                      const resp = await configApi.getConfigByKey('inventory');
                      if (resp && resp.success && resp.configured) {
                        const normalizedDto = resp.data?.dto?.fields ? resp.data.dto : (resp.data?.dto?.dto || null);
                        setConfigStatus(prev => ({
                          ...prev,
                          products: {
                            ...prev.products,
                            hasInventory: true,
                            lastUpdated: resp.updatedAt || null,
                            inventoryDto: normalizedDto || prev.products?.inventoryDto,
                            inventorySource: resp.data?.defaultSource || prev.products?.inventorySource
                          }
                        }));
                      }
                    } catch (e) {
                      // Failed to load inventory config
                    } finally {
                      setInventoryConfigModalOpen(true);
                    }
                  }}
                >
                  <Text variant="headingSm" fontWeight="bold" color="subdued">
                    Inventory Configuration {configStatus.products.hasInventory ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                  </Text>
                </Button>
                )}

                {serviceGates.prices && (
                  <Button
                    size="large"
                    variant="secondary"
                    fullWidth={false}
                    disabled={!gatesReady}
                    onClick={async () => {
                    keepAlive();
                    try {
                      const resp = await configApi.getConfigByKey('pricelevels');
                      if (resp && resp.success && resp.configured) {
                        const normalizedDto = resp.data?.dto?.fields ? resp.data.dto : (resp.data?.dto?.dto || null);
                        setConfigStatus(prev => ({
                          ...prev,
                          products: {
                            ...prev.products,
                            hasPriceLevels: true,
                            lastUpdated: resp.updatedAt || null,
                            priceLevelsDto: normalizedDto || prev.products?.priceLevelsDto,
                            priceLevelsSource: resp.data?.defaultSource || prev.products?.priceLevelsSource
                          }
                        }));
                      }
                    } catch (e) {
                      // Failed to load price levels config
                    } finally {
                      setPriceLevelsConfigModalOpen(true);
                    }
                  }}
                >
                  <Text variant="headingSm" fontWeight="bold" color="subdued">
                    Price Levels {configStatus.products.hasPriceLevels ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                  </Text>
                </Button>
                )}
              </div>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* 20px spacing div */}
        <div style={{ height: '20px' }}></div>

        {/* Customer Configuration */}
        {serviceGates.customers && (
          <Layout.Section>
            <Card>
              <BlockStack gap="loose">
                <div>
                  <Text variant="headingMd" fontWeight="bold" as="h2">
                    Customer Configuration
                  </Text>
                  <Text variant="bodyMd" as="p" color="subdued">
                    Configure customer field mappings and transformation logic
                  </Text>
                </div>

                <div className="action-buttons">
                  <Button
                    size="large"
                    disabled={!gatesReady}
                    onClick={async () => {
                      keepAlive();
                      try {
                        // Load latest customers config from backend
                        const resp = await configApi.getConfigByKey('customers');
                        if (resp && resp.success && resp.configured) {
                          const normalizedDto = resp.data?.dto?.fields
                            ? resp.data.dto
                            : (resp.data?.dto?.dto || null);
                          setConfigStatus(prev => ({
                            ...prev,
                            customers: {
                              hasConfig: true,
                              lastUpdated: resp.updatedAt || null,
                              status: 'complete',
                              mappings: prev.customers.mappings,
                              sourceSample: resp.data?.defaultSource || prev.customers.sourceSample,
                              dto: normalizedDto || prev.customers.dto
                            }
                          }));
                        }
                      } catch (e) {
                        // Failed to load customers config
                      } finally {
                        setCustomersModalOpen(true);
                      }
                    }}
                    variant="secondary"
                    fullWidth={false}
                    style={{ width: '100%' }}
                  >
                    <Text variant="headingSm" fontWeight="bold" color="subdued">
                      Configure Customers {configStatus.customers.hasConfig ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                    </Text>
                  </Button>


                </div>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* dynamic services section removed per request */}

        {/* Orders Configuration */}
        <Layout.Section>
          <Card>
            <BlockStack gap="loose">
              <div>
                <Text variant="headingMd" fontWeight="bold" as="h2">
                  Order Configuration
                </Text>
                <Text variant="bodyMd" as="p" color="subdued">
                  Configure order field mappings and synchronization settings
                </Text>
              </div>
              <div className="action-buttons-6col">
                <Button
                  size="large"
                  disabled={!gatesReady || !serviceGates.orders}
                  variant="secondary"
                  fullWidth={false}
                  onClick={async () => {
                    keepAlive();
                    try {
                      const resp = await configApi.getConfigByKey('orders');
                      if (resp && resp.success && resp.configured) {
                        const normalizedDto = resp.data?.dto?.fields ? resp.data.dto : (resp.data?.dto?.dto || null);
                        setConfigStatus(prev => ({
                          ...prev,
                          orders: {
                            ...prev.orders,
                            hasConfig: true,
                            lastUpdated: resp.updatedAt || null,
                            dto: normalizedDto || prev.orders?.dto,
                            sourceSample: resp.data?.defaultSource || prev.orders?.sourceSample
                          }
                        }));
                      }
                    } catch (e) {
                      // Failed to load orders config
                    } finally {
                      setOrdersConfigModalOpen(true);
                    }
                  }}
                >
                  <Text variant="headingSm" fontWeight="bold" color="subdued">
                    Configure Orders {configStatus.orders.hasConfig ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                  </Text>
                </Button>
                <Button
                  size="large"
                  disabled={!gatesReady || !serviceGates.orders}
                  variant="secondary"
                  fullWidth={false}
                  onClick={async () => {
                    keepAlive();
                    try {
                      // Load order settings from orders DTO
                      const resp = await configApi.getConfigByKey('orders');
                      const data = resp?.data || {};
                      const dto = data.dto?.fields ? data.dto : (data.dto?.dto || null);
                      const settings = dto?.settings || {};
                      let orderSettings = settings.order || {};

                      // Prepayment DTO stores at settings.prepayment; modal expects order.prepayment
                      if (settings.prepayment) {
                        orderSettings = {
                          ...orderSettings,
                          prepayment: {
                            enabled: settings.prepayment.enabled ?? !!settings.prepayment.batchPrefix,
                            batchPrefix: settings.prepayment.batchPrefix || 'WEB',
                            batchFrequency: settings.prepayment.batchFrequency || 'weekly',
                            ...settings.prepayment
                          }
                        };
                      }

                      // Also check SAGE config for existing customerConfig and orderConfig
                      // (for backward compatibility - settings may still be in sage config)
                      try {
                        const sageResp = await configApi.getSageConfig();
                        if (sageResp?.success && sageResp?.config) {
                          const sageConfig = sageResp.config;
                          // Merge customerConfig from SAGE into order settings
                          if (sageConfig.customerConfig) {
                            orderSettings = {
                              ...orderSettings,
                              customerMode: orderSettings.customerMode || sageConfig.customerConfig.mode,
                              fixedCustomerNumber: orderSettings.fixedCustomerNumber || sageConfig.customerConfig.fixedCustomerNumber,
                              createStrategy: orderSettings.createStrategy || sageConfig.customerConfig.createStrategy,
                              customerPrefix: orderSettings.customerPrefix || sageConfig.customerConfig.customerPrefix,
                              customerDefaults: orderSettings.customerDefaults || sageConfig.customerConfig.customerDefaults
                            };
                          }
                          // Merge orderConfig from SAGE into order settings
                          if (sageConfig.orderConfig) {
                            orderSettings = {
                              ...orderSettings,
                              defaultLocation: orderSettings.defaultLocation || sageConfig.orderConfig.defaultLocation,
                              defaultPriceList: orderSettings.defaultPriceList || sageConfig.orderConfig.defaultPriceList,
                              termsCode: orderSettings.termsCode || sageConfig.orderConfig.termsCode,
                              taxGroup: orderSettings.taxGroup || sageConfig.orderConfig.taxGroup,
                              paymentCode: orderSettings.paymentCode || sageConfig.orderConfig.paymentCode,
                              shipToPrefix: orderSettings.shipToPrefix || sageConfig.orderConfig.shipToPrefix,
                              shippingChargeCode: orderSettings.shippingChargeCode || sageConfig.orderConfig.shippingChargeCode
                            };
                          }
                        }
                      } catch (sageErr) {
                        // SAGE config not available or error - continue with orders DTO settings only
                      }

                      setOrdersSettingsState({ order: orderSettings });
                      // Update configStatus.orders.dto to include the full DTO with destination
                      // This ensures the destination is available for the modal wrapper
                      if (dto) {
                        setConfigStatus(prev => ({
                          ...prev,
                          orders: {
                            ...prev.orders,
                            dto: dto,
                            lastUpdated: resp.updatedAt || prev.orders?.lastUpdated || null
                          }
                        }));
                      }
                    } catch (e) {
                      // Failed to load orders settings
                      setOrdersSettingsState({ order: {} });
                    } finally {
                      setOrdersSettingsModalOpen(true);
                    }
                  }}
                >
                  <Text variant="headingSm" fontWeight="bold" color="subdued">
                    Order Settings
                  </Text>
                </Button>
                {(serviceGates.qbo_customer_deposits || configStatus.qbo_customer_deposits?.hasConfig) && (
                  <Button
                    size="large"
                    disabled={!gatesReady}
                    variant="secondary"
                    fullWidth={false}
                    onClick={async () => {
                      await loadQboCustomerDepositsConfig();
                      setQboCustomerDepositsModalOpen(true);
                    }}
                  >
                    <Text variant="headingSm" fontWeight="bold" color="subdued">
                      QBO Customer Deposits {configStatus.qbo_customer_deposits?.hasConfig ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                    </Text>
                  </Button>
                )}




              </div>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* 20px spacing div */}
        <div style={{ height: '20px' }}></div>

        {/* Order Update Configuration */}
        {(serviceGates.order_updates || configStatus.order_updates?.hasConfig) && (
          <>
            <Layout.Section>
              <Card>
                <BlockStack gap="loose">
                  <div>
                    <Text variant="headingMd" fontWeight="bold" as="h2">
                      Order Update Configuration
                    </Text>
                    <Text variant="bodyMd" as="p" color="subdued">
                      Configure order cancellation and update mappings
                    </Text>
                  </div>
                  <div className="action-buttons-4col">
                    <Button
                      size="large"
                      variant="secondary"
                      fullWidth={false}
                      disabled={!gatesReady || !serviceGates.order_updates}
                      onClick={async () => {
                        await keepAlive();
                        try {
                          const resp = await configApi.getConfigByKey('order_updates');
                          if (resp && resp.success && resp.configured) {
                            const normalizedDto = resp.data?.dto?.fields ? resp.data.dto : (resp.data?.dto?.dto || null);
                            setConfigStatus(prev => ({
                              ...prev,
                              order_updates: {
                                ...prev.order_updates,
                                hasConfig: true,
                                lastUpdated: resp.updatedAt || null,
                                status: 'complete',
                                dto: normalizedDto || prev.order_updates?.dto,
                                sourceSample: resp.data?.defaultSource || prev.order_updates?.sourceSample
                              }
                            }));
                          }
                        } catch (e) {
                          // Failed to load order update config
                        } finally {
                          setOrderUpdatesModalOpen(true);
                        }
                      }}
                    >
                      <Text variant="headingSm" fontWeight="bold" color="subdued">
                        Configure Order Updates {configStatus.order_updates?.hasConfig ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                      </Text>
                    </Button>
                  </div>
                </BlockStack>
              </Card>
            </Layout.Section>

            <div style={{ height: '20px' }}></div>
          </>
        )}

        {/* Fulfillment Configuration */}
        <Layout.Section>
          <Card>
            <BlockStack gap="loose">
              <div>
                <Text variant="headingMd" fontWeight="bold" as="h2">
                  Fulfillment Configuration
                </Text>
                <Text variant="bodyMd" as="p" color="subdued">
                  Configure fulfillment synchronization settings and saved search mappings
                </Text>
              </div>

              <div className="action-buttons-4col">
                {(serviceGates.fulfillments || serviceGates.fulfillment) && (
                  <Button
                    size="large"
                    variant="secondary"
                    fullWidth={false}
                    disabled={!gatesReady}
                    onClick={() => openFulfillmentConfig('fulfillments')}
                  >
                    <Text variant="headingSm" fontWeight="bold" color="subdued">
                      Configure Fulfillment {configStatus.fulfillments?.hasSavedSearch ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                    </Text>
                  </Button>
                )}
                {serviceGates.fulfilled_orders && (
                  <Button
                    size="large"
                    variant="secondary"
                    fullWidth={false}
                    disabled={!gatesReady}
                    onClick={() => openFulfillmentConfig('fulfilled_orders')}
                  >
                    <Text variant="headingSm" fontWeight="bold" color="subdued">
                      Configure Fulfilled Orders {configStatus.fulfilled_orders?.hasSavedSearch ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                    </Text>
                  </Button>
                )}
                {(serviceGates.qbo_shipment_invoices || configStatus.qbo_shipment_invoices?.hasConfig) && (
                  <Button
                    size="large"
                    variant="secondary"
                    fullWidth={false}
                    disabled={!gatesReady}
                    onClick={async () => {
                      await loadQboShipmentInvoicesConfig();
                      setQboShipmentInvoicesModalOpen(true);
                    }}
                  >
                    <Text variant="headingSm" fontWeight="bold" color="subdued">
                      QBO Shipment Invoices {configStatus.qbo_shipment_invoices?.hasConfig ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                    </Text>
                  </Button>
                )}
              </div>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* 20px spacing div */}
        <div style={{ height: '20px' }}></div>

        {/* Returns and Refunds Configuration */}
        <Layout.Section>
          <Card>
            <BlockStack gap="loose">
              <div>
                <Text variant="headingMd" fontWeight="bold" as="h2">
                  Returns and Refunds Configuration
                </Text>
                <Text variant="bodyMd" as="p" color="subdued">
                  Configure web-order returns and refunds
                </Text>
              </div>

              <div className="action-buttons-4col">
                <Button
                  size="large"
                  variant="secondary"
                  fullWidth={false}
                  disabled={!gatesReady || !serviceGates.returns}
                  onClick={async () => {
                    await keepAlive();
                    try {
                      const resp = await configApi.getConfigByKey(returnsConfigKey);
                      if (resp && resp.success && resp.configured) {
                        const normalizedDto = resp.data?.dto?.fields ? resp.data.dto : (resp.data?.dto?.dto || null);
                        setConfigStatus(prev => ({
                          ...prev,
                          [returnsConfigKey]: {
                            hasSavedSearch: true,
                            lastUpdated: resp.updatedAt || null,
                            status: 'complete',
                            dto: normalizedDto || prev[returnsConfigKey]?.dto,
                            sourceSample: resp.data?.defaultSource || prev[returnsConfigKey]?.sourceSample
                          }
                        }));
                      }
                    } catch (e) {
                      // Failed to load returns config
                    } finally {
                      setReturnsModalOpen(true);
                    }
                  }}
                >
                  <Text variant="headingSm" fontWeight="bold" color="subdued">
                    Configure Returns {activeReturnsStatus?.hasSavedSearch ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                  </Text>
                </Button>

                <Button
                  size="large"
                  variant="secondary"
                  fullWidth={false}
                  disabled={!gatesReady || !serviceGates.refunds}
                  onClick={async () => {
                    await keepAlive();
                    try {
                      const resp = await configApi.getConfigByKey('refunds');
                      if (resp && resp.success && resp.configured) {
                        const normalizedDto = resp.data?.dto?.fields ? resp.data.dto : (resp.data?.dto?.dto || null);
                        setConfigStatus(prev => ({
                          ...prev,
                          refunds: {
                            hasSavedSearch: true,
                            lastUpdated: resp.updatedAt || null,
                            status: 'complete',
                            dto: normalizedDto || prev.refunds?.dto,
                            sourceSample: resp.data?.defaultSource || prev.refunds?.sourceSample
                          }
                        }));
                      }
                    } catch (e) {
                      // Failed to load refunds config
                    } finally {
                      setRefundsModalOpen(true);
                    }
                  }}
                >
                  <Text variant="headingSm" fontWeight="bold" color="subdued">
                    Configure Refunds {configStatus.refunds?.hasSavedSearch ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                  </Text>
                </Button>

                {(serviceGates.qbo_refunds || configStatus.qbo_refunds?.hasConfig) && (
                  <Button
                    size="large"
                    variant="secondary"
                    fullWidth={false}
                    disabled={!gatesReady}
                    onClick={async () => {
                      await loadQboRefundsConfig();
                      setQboRefundsModalOpen(true);
                    }}
                  >
                    <Text variant="headingSm" fontWeight="bold" color="subdued">
                      QBO Refunds {configStatus.qbo_refunds?.hasConfig ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                    </Text>
                  </Button>
                )}

                {(serviceGates.qbo_fee_journals || configStatus.qbo_fee_journals?.hasConfig) && (
                  <Button
                    size="large"
                    variant="secondary"
                    fullWidth={false}
                    disabled={!gatesReady}
                    onClick={async () => {
                      await loadQboFeeJournalsConfig();
                      setQboFeeJournalsModalOpen(true);
                    }}
                  >
                    <Text variant="headingSm" fontWeight="bold" color="subdued">
                      QBO Settlement / Merchant Fees {configStatus.qbo_fee_journals?.hasConfig ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                    </Text>
                  </Button>
                )}
              </div>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* 20px spacing div */}
        <div style={{ height: '20px' }}></div>

        {/* Vendor Bills Configuration */}
        {(serviceGates.qbo_vendor_bills || configStatus.qbo_vendor_bills?.hasConfig) && (
          <>
            <Layout.Section>
              <Card>
                <BlockStack gap="loose">
                  <div>
                    <Text variant="headingMd" fontWeight="bold" as="h2">
                      Vendor Bills Configuration
                    </Text>
                    <Text variant="bodyMd" as="p" color="subdued">
                      Configure approved vendor invoice bill mappings
                    </Text>
                  </div>

                  <div className="action-buttons-4col">
                    <Button
                      size="large"
                      variant="secondary"
                      fullWidth={false}
                      disabled={!gatesReady || !(serviceGates.qbo_vendor_bills || configStatus.qbo_vendor_bills?.hasConfig)}
                      onClick={async () => {
                        await loadQboVendorBillsConfig();
                        setQboVendorBillsModalOpen(true);
                      }}
                    >
                      <Text variant="headingSm" fontWeight="bold" color="subdued">
                        QBO Vendor Bills {configStatus.qbo_vendor_bills?.hasConfig ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                      </Text>
                    </Button>
                  </div>
                </BlockStack>
              </Card>
            </Layout.Section>

            <div style={{ height: '20px' }}></div>
          </>
        )}

        {/* Cash Transactions Configuration */}
        <Layout.Section>
          <Card>
            <BlockStack gap="loose">
              <div>
                <Text variant="headingMd" fontWeight="bold" as="h2">
                  Cash Transactions Configuration
                </Text>
                <Text variant="bodyMd" as="p" color="subdued">
                  Configure POS cash sales, cash refunds, and exchanges
                </Text>
              </div>

              <div className="action-buttons-4col">
                <Button
                  size="large"
                  variant="secondary"
                  fullWidth={false}
                  disabled={!gatesReady || !serviceGates.cash_sales}
                  onClick={() => loadServiceConfig('cash_sales', setCashSalesModalOpen)}
                >
                  <Text variant="headingSm" fontWeight="bold" color="subdued">
                    Configure Cash Sales {configStatus.cash_sales?.hasSavedSearch ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                  </Text>
                </Button>

                <Button
                  size="large"
                  variant="secondary"
                  fullWidth={false}
                  disabled={!gatesReady || !serviceGates.cash_sales}
                  onClick={loadCashSalesSettings}
                >
                  <Text variant="headingSm" fontWeight="bold" color="subdued">
                    Cash Sale Settings
                  </Text>
                </Button>

                <Button
                  size="large"
                  variant="secondary"
                  fullWidth={false}
                  disabled={!gatesReady || !serviceGates.cash_refunds}
                  onClick={() => loadServiceConfig('cash_refunds', setCashRefundsModalOpen)}
                >
                  <Text variant="headingSm" fontWeight="bold" color="subdued">
                    Configure Cash Refunds {configStatus.cash_refunds?.hasSavedSearch ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                  </Text>
                </Button>

                <Button
                  size="large"
                  variant="secondary"
                  fullWidth={false}
                  disabled={!gatesReady || !serviceGates.exchanges}
                  onClick={() => loadServiceConfig('exchanges', setExchangesModalOpen)}
                >
                  <Text variant="headingSm" fontWeight="bold" color="subdued">
                    Configure Exchanges {configStatus.exchanges?.hasSavedSearch ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                  </Text>
                </Button>
              </div>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* 20px spacing div */}
        <div style={{ height: '20px' }}></div>

        {/* Payout Configuration */}
        {(serviceGates.payouts || configStatus.payouts?.hasSavedSearch) && (
          <>
            <Layout.Section>
              <Card>
                <BlockStack gap="loose">
                  <div>
                    <Text variant="headingMd" fontWeight="bold" as="h2">
                      Payout Configuration
                    </Text>
                    <Text variant="bodyMd" as="p" color="subdued">
                      Configure Wayfair payout invoice payment application mappings
                    </Text>
                  </div>

                  <div className="action-buttons-4col">
                    <Button
                      size="large"
                      variant="secondary"
                      fullWidth={false}
                      disabled={!gatesReady || !serviceGates.payouts}
                      onClick={() => loadServiceConfig('payouts', setPayoutsModalOpen)}
                    >
                      <Text variant="headingSm" fontWeight="bold" color="subdued">
                        Configure Payouts {configStatus.payouts?.hasSavedSearch ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                      </Text>
                    </Button>

                    <Button
                      size="large"
                      variant="secondary"
                      fullWidth={false}
                      disabled={!gatesReady || !serviceGates.payouts}
                      onClick={loadPayoutSettings}
                    >
                      <Text variant="headingSm" fontWeight="bold" color="subdued">
                        Payout Settings
                      </Text>
                    </Button>
                  </div>
                </BlockStack>
              </Card>
            </Layout.Section>

            <div style={{ height: '20px' }}></div>
          </>
        )}

        {/* Payout Settlements Configuration */}
        {(serviceGates.payout_settlements || configStatus.payout_settlements?.hasSavedSearch) && (
          <>
            <Layout.Section>
              <Card>
                <BlockStack gap="loose">
                  <div>
                    <Text variant="headingMd" fontWeight="bold" as="h2">
                      Payout Settlements Configuration
                    </Text>
                    <Text variant="bodyMd" as="p" color="subdued">
                      Configure payout settlement DTO mapping to NetSuite Bank Deposit records
                    </Text>
                  </div>

                  <div className="action-buttons-4col">
                    <Button
                      size="large"
                      variant="secondary"
                      fullWidth={false}
                      disabled={!gatesReady || !serviceGates.payout_settlements}
                      onClick={() => loadServiceConfig('payout_settlements', setPayoutSettlementsModalOpen)}
                    >
                      <Text variant="headingSm" fontWeight="bold" color="subdued">
                        Configure Payout Settlements {configStatus.payout_settlements?.hasSavedSearch ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                      </Text>
                    </Button>
                  </div>
                </BlockStack>
              </Card>
            </Layout.Section>

            <div style={{ height: '20px' }}></div>
          </>
        )}

        {/* Images Configuration */}
        <Layout.Section>
          <Card>
            <BlockStack gap="loose">
              <div>
                <Text variant="headingMd" fontWeight="bold" as="h2">
                  Images Configuration
                </Text>
                <Text variant="bodyMd" as="p" color="subdued">
                  Configure image synchronization settings and saved search mappings
                </Text>
              </div>

              <div className="action-buttons-4col">
                <Button
                  size="large"
                  variant="secondary"
                  fullWidth={false}
                  disabled={!gatesReady || !serviceGates.images}
                  onClick={async () => {
                    await keepAlive();
                    try {
                      const resp = await configApi.getConfigByKey('images');
                      if (resp && resp.success && resp.configured) {
                        setConfigStatus(prev => ({
                          ...prev,
                          images: { ...prev.images, hasSavedSearch: true, lastUpdated: resp.updatedAt || null, status: 'complete' }
                        }));
                        // Normalize and keep for modal
                        const d = resp.data || {};
                        const dto = d.dto?.fields ? d.dto : (d.dto?.dto || null);
                        // Ensure rootPath is in the DTO settings for the ConfigurationModal to find it
                        if (dto && dto.settings && dto.settings.rootPath) {
                          // rootPath is already in DTO settings, use as-is
                        } else if (dto && dto.settings) {
                          // Add rootPath to DTO settings if it exists elsewhere
                          dto.settings.rootPath = dto.settings.rootPath || 'items[*]';
                        }
                        setImagesConfig({
                          dto: dto,
                          sourceSample: d.defaultSource || null,
                          settings: d.settings || {}
                        });
                      }
                    } catch (e) { /* open modal anyway */ }
                    setImagesModalOpen(true);
                  }}
                >
                  <Text variant="headingSm" fontWeight="bold" color="subdued">
                    Image Settings {configStatus.images?.hasSavedSearch ? <span className="tick-mark">✓</span> : <span className="incomplete-mark">○</span>}
                  </Text>
                </Button>
              </div>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* 20px spacing div */}
        <div style={{ height: '20px' }}></div>

        {/* Help Section */}
        <Layout.Section>
          <HelpSection />
        </Layout.Section>
      </Layout>

      {/* Modals */}
      <NetsuiteM2MModal
        open={netsuiteM2MOpen}
        onClose={()=>{ setNetsuiteM2MOpen(false); setNetsuiteM2MConfig(null); }}
        onSaved={()=>loadSavedConfigurations()}
        config={netsuiteM2MConfig || {}}
      />

      <AdobeCommerceCredentialsModal
        open={adobeModalOpen}
        onClose={() => {
          setAdobeModalOpen(false);
          setAdobeCredentials(null);
        }}
        onSave={handleAdobeSave}
        config={adobeCredentials || {}}
      />
      {/* Removed AdobeOAuth2Modal */}

      <ShopifyCredentialsModal
        open={shopifyModalOpen}
        onClose={() => {
          setShopifyModalOpen(false);
          setShopifyCredentials(null);
        }}
        onSave={handleShopifySave}
        config={shopifyCredentials || {}}
      />

      <SageCredentialsModal
        open={sageModalOpen}
        onClose={() => setSageModalOpen(false)}
        onSave={handleSageSave}
        config={sageCredentials || {}}
      />

      <BearerTokenCredentialsModal
        open={kdsModalOpen}
        onClose={() => {
          setKdsModalOpen(false);
          setKdsCredentials(null);
        }}
        onSave={handleKdsSave}
        configKey="kds"
        title="KDS Connection"
        defaultConnectionName="Graeter's KDS"
        defaultBaseUrl="http://localhost:3100"
        defaultTestPath="/v1/health"
        config={kdsCredentials || {}}
      />

      <WayfairCredentialsModal
        open={wayfairModalOpen}
        onClose={() => {
          setWayfairModalOpen(false);
          setWayfairCredentials(null);
        }}
        onSave={handleWayfairSave}
        config={wayfairCredentials || {}}
      />

      <WayfairAgentCredentialsModal
        open={wayfairAgentModalOpen}
        onClose={() => {
          setWayfairAgentModalOpen(false);
          setWayfairAgentCredentials(null);
        }}
        onSave={handleWayfairAgentSave}
        config={wayfairAgentCredentials || {}}
      />

      <QuickBooksCredentialsModal
        open={quickBooksModalOpen}
        onClose={() => {
          setQuickBooksModalOpen(false);
          setQuickBooksCredentials(null);
        }}
        onSave={handleQuickBooksSave}
        config={quickBooksCredentials || {}}
      />

      <ConfigurationModal
        open={customersModalOpen}
        onClose={() => setCustomersModalOpen(false)}
        onSave={handleCustomersSave}
        keyId="customers"
        entityLabel="Customers"
        config={configStatus.customers.hasConfig ? {
          mappings: configStatus.customers.mappings || [],
          sourceSample: configStatus.customers.sourceSample || null,
          dto: configStatus.customers.dto || null
        } : {}}
      />

      <ConfigurationModal
        open={ordersConfigModalOpen}
        onClose={() => setOrdersConfigModalOpen(false)}
        onSave={handleOrdersConfigSave}
        keyId="orders"
        entityLabel="Orders"
        config={configStatus.orders.hasConfig ? {
          sourceSample: configStatus.orders.sourceSample || null,
          dto: configStatus.orders.dto || null
        } : {}}
      />

      <ConfigurationModal
        open={qboCustomerDepositsModalOpen}
        onClose={() => setQboCustomerDepositsModalOpen(false)}
        onSave={handleQboCustomerDepositsSave}
        keyId="qbo_customer_deposits"
        entityLabel="QBO Customer Deposits"
        config={configStatus.qbo_customer_deposits?.hasConfig ? {
          sourceSample: configStatus.qbo_customer_deposits.sourceSample || null,
          dto: configStatus.qbo_customer_deposits.dto || null
        } : {}}
      />

      <ConfigurationModal
        open={qboShipmentInvoicesModalOpen}
        onClose={() => setQboShipmentInvoicesModalOpen(false)}
        onSave={handleQboShipmentInvoicesSave}
        keyId="qbo_shipment_invoices"
        entityLabel="QBO Shipment Invoices"
        config={configStatus.qbo_shipment_invoices?.hasConfig ? {
          sourceSample: configStatus.qbo_shipment_invoices.sourceSample || null,
          dto: configStatus.qbo_shipment_invoices.dto || null
        } : {}}
      />

      <ConfigurationModal
        open={qboRefundsModalOpen}
        onClose={() => setQboRefundsModalOpen(false)}
        onSave={handleQboRefundsSave}
        keyId="qbo_refunds"
        entityLabel="QBO Refunds"
        config={configStatus.qbo_refunds?.hasConfig ? {
          sourceSample: configStatus.qbo_refunds.sourceSample || null,
          dto: configStatus.qbo_refunds.dto || null
        } : {}}
      />

      <ConfigurationModal
        open={qboFeeJournalsModalOpen}
        onClose={() => setQboFeeJournalsModalOpen(false)}
        onSave={handleQboFeeJournalsSave}
        keyId="qbo_fee_journals"
        entityLabel="QBO Settlement / Merchant Fees"
        config={configStatus.qbo_fee_journals?.hasConfig ? {
          sourceSample: configStatus.qbo_fee_journals.sourceSample || null,
          dto: configStatus.qbo_fee_journals.dto || null
        } : {}}
      />

      <ConfigurationModal
        open={qboVendorBillsModalOpen}
        onClose={() => setQboVendorBillsModalOpen(false)}
        onSave={handleQboVendorBillsSave}
        keyId="qbo_vendor_bills"
        entityLabel="QBO Vendor Bills"
        config={configStatus.qbo_vendor_bills?.hasConfig ? {
          sourceSample: configStatus.qbo_vendor_bills.sourceSample || null,
          dto: configStatus.qbo_vendor_bills.dto || null
        } : {}}
      />

      <OrdersSettingsModalWrapper
        destination={configStatus.orders?.dto?.destination || currentWorkspace?.integrations?.backend?.provider}
        source={configStatus.orders?.dto?.source}
        open={ordersSettingsModalOpen}
        onClose={() => setOrdersSettingsModalOpen(false)}
        onSave={handleOrdersSettingsSave}
        config={ordersSettingsState}
      />

      <ConfigurationModal
        open={orderUpdatesModalOpen}
        onClose={() => setOrderUpdatesModalOpen(false)}
        onSave={handleOrderUpdatesSave}
        keyId="order_updates"
        entityLabel="Order Updates"
        config={configStatus.order_updates?.dto ? {
          sourceSample: configStatus.order_updates.sourceSample || null,
          dto: configStatus.order_updates.dto || null
        } : {}}
      />

      <NetSuiteCashSalesSettingsModal
        source={configStatus.cash_sales?.dto?.source}
        open={cashSalesSettingsModalOpen}
        onClose={() => setCashSalesSettingsModalOpen(false)}
        onSave={handleCashSalesSettingsSave}
        config={cashSalesSettingsState}
      />



      <ConfigurationModal
        open={itemsConfigModalOpen}
        onClose={() => setItemsConfigModalOpen(false)}
        onSave={handleItemsConfigSave}
        keyId="products"
        entityLabel="Items"
        config={configStatus.products.itemsDto ? {
          dto: configStatus.products.itemsDto,
          sourceSample: configStatus.products.itemsSource || null
        } : {}}
      />

      <ProductSettingsModal
        open={productSettingsModalOpen}
        onClose={() => setProductSettingsModalOpen(false)}
        onSave={handleProductSettingsSave}
        config={configStatus.products.productSettings || {}}
      />

      <ConfigurationModal
        open={imagesModalOpen}
        onClose={() => setImagesModalOpen(false)}
        onSave={handleImagesSave}
        keyId="images"
        entityLabel="Images"
        config={imagesConfig ? {
          dto: imagesConfig.dto || null,
          sourceSample: imagesConfig.sourceSample || null,
          settings: imagesConfig.settings || {}
        } : {}}
      />

      <ConfigurationModal
        open={inventoryConfigModalOpen}
        onClose={() => setInventoryConfigModalOpen(false)}
        onSave={handleInventoryConfigSave}
        keyId="inventory"
        entityLabel="Inventory"
        config={configStatus.products.inventoryDto ? {
          dto: configStatus.products.inventoryDto,
          sourceSample: configStatus.products.inventorySource || null
        } : {}}
      />

      <ConfigurationModal
        open={priceLevelsConfigModalOpen}
        onClose={() => setPriceLevelsConfigModalOpen(false)}
        onSave={handlePriceLevelsConfigSave}
        keyId="pricelevels"
        entityLabel="Price Levels"
        config={configStatus.products.priceLevelsDto ? {
          dto: configStatus.products.priceLevelsDto,
          sourceSample: configStatus.products.priceLevelsSource || null
        } : {}}
      />

      {/* Removed OrdersGeneralModal; orders are configured via the generic ConfigurationModal above */}





      {/* Images modal removed; button disabled until requirements finalized */}

      <ConfigurationModal
        open={fulfillmentModalOpen}
        onClose={() => setFulfillmentModalOpen(false)}
        onSave={handleFulfillmentSave}
        keyId={fulfillmentModalKey}
        entityLabel="Fulfillment"
        config={configStatus[fulfillmentModalKey]?.dto ? {
          sourceSample: configStatus[fulfillmentModalKey].sourceSample || null,
          dto: configStatus[fulfillmentModalKey].dto || null
        } : {}}
      />

      <ConfigurationModal
        open={returnsModalOpen}
        onClose={() => setReturnsModalOpen(false)}
        onSave={handleReturnsSave}
        keyId={returnsConfigKey}
        entityLabel="Returns"
        config={activeReturnsStatus?.dto ? {
          sourceSample: activeReturnsStatus.sourceSample || null,
          dto: activeReturnsStatus.dto || null
        } : {}}
      />

      <ConfigurationModal
        open={refundsModalOpen}
        onClose={() => setRefundsModalOpen(false)}
        onSave={handleRefundsSave}
        keyId="refunds"
        entityLabel="Refunds"
        config={configStatus.refunds?.dto ? {
          sourceSample: configStatus.refunds.sourceSample || null,
          dto: configStatus.refunds.dto || null
        } : {}}
      />

      <ConfigurationModal
        open={cashSalesModalOpen}
        onClose={() => setCashSalesModalOpen(false)}
        onSave={handleCashSalesSave}
        keyId="cash_sales"
        entityLabel="Cash Sales"
        config={configStatus.cash_sales?.dto ? {
          sourceSample: configStatus.cash_sales.sourceSample || null,
          dto: configStatus.cash_sales.dto || null
        } : {}}
      />

      <ConfigurationModal
        open={cashRefundsModalOpen}
        onClose={() => setCashRefundsModalOpen(false)}
        onSave={handleCashRefundsSave}
        keyId="cash_refunds"
        entityLabel="Cash Refunds"
        config={configStatus.cash_refunds?.dto ? {
          sourceSample: configStatus.cash_refunds.sourceSample || null,
          dto: configStatus.cash_refunds.dto || null
        } : {}}
      />

      <ConfigurationModal
        open={exchangesModalOpen}
        onClose={() => setExchangesModalOpen(false)}
        onSave={handleExchangesSave}
        keyId="exchanges"
        entityLabel="Exchanges"
        config={configStatus.exchanges?.dto ? {
          sourceSample: configStatus.exchanges.sourceSample || null,
          dto: configStatus.exchanges.dto || null
        } : {}}
      />

      <ConfigurationModal
        open={payoutsModalOpen}
        onClose={() => setPayoutsModalOpen(false)}
        onSave={handlePayoutsSave}
        keyId="payouts"
        entityLabel="Payouts"
        config={configStatus.payouts?.dto ? {
          sourceSample: configStatus.payouts.sourceSample || null,
          dto: configStatus.payouts.dto || null
        } : {}}
      />

      <ConfigurationModal
        open={payoutSettlementsModalOpen}
        onClose={() => setPayoutSettlementsModalOpen(false)}
        onSave={handlePayoutSettlementsSave}
        keyId="payout_settlements"
        entityLabel="Payout Settlements"
        config={configStatus.payout_settlements?.dto ? {
          sourceSample: configStatus.payout_settlements.sourceSample || null,
          dto: configStatus.payout_settlements.dto || null
        } : {}}
      />

      <PayoutSettingsModal
        open={payoutSettingsModalOpen}
        onClose={() => setPayoutSettingsModalOpen(false)}
        onSave={handlePayoutSettingsSave}
        config={payoutSettingsState}
      />
    </Page>
  );
}
